// Executes the structured action returned by processAICommand against the
// REAL CompleteTourneyLive Firebase shape. Every handler is a small atomic
// write — failures roll up as { success:false, message } and get logged to
// tournaments/{id}/aiLog/{ts_rand}.
//
// Data model recap:
//   tournaments/{id}/info.scoringEnabled
//   tournaments/{id}/players/{playerId}.{name, handicap, ...}
//   tournaments/{id}/flights/{flightId}/teams/{teamId}/info
//     .name, .players[], .startingHole, .opponentId, .handicap
//   tournaments/{id}/teeTimes/{day}/{flightId} = "8:00 AM"     (per-flight)
//   tournaments/{id}/teeTimes/{day}/waves/{waveKey}/startTime  (shotgun)

import {
  ref, get, set, update, remove, serverTimestamp, push,
} from 'firebase/database'
import { tref, updateTenant } from '../lib/firebase.js'
import { addRegistration, advanceBracket, assignShotgunHolesByMatch, assignShotgunHoles, autoFlightRegistrations, autoPairAllFlights, bulkAddRegistrations, closeTournamentWithResults, createTournament, deleteRegistration, ensureTournamentInfo, generateBracket, setBracketMatch, setTeamHoleScore, updateRegistration, updateTournamentInfo, snapshotNetHandicaps, setTvNetMode } from '../lib/data.js'
import { submitQualifyingEventResult } from '../lib/qualifyingEvents.js'
import { demoRegistrationRows, createDemoTournament } from '../lib/demos.js'
import { calcCourseHandicap } from '../lib/handicap.js'
import { buildHandicapEntry, resolveAllowSelfReported, collectHandicapReview } from '../lib/handicapEntry.js'
import { planGhinPull, resolveRegistrationPlayer, summarizeHandicapReview } from '../lib/aiHandicapIntents.js'
import { lookupGhinHandicap } from '../lib/ghinLookup.js'
import CLUB from '../config/club.js'

// ─── PBCC default template ──────────────────────────────────────────────────
//
// When a director says "create a 4-ball" / "set up a 4-ball" / "like the
// demo", we copy from PBCC's canonical 4-Ball template instead of asking
// any setup questions. The AI just needs name + date.
//
// PBCC_TEMPLATE_SENTINEL is what the AI puts in `sourceTournamentId` when
// it wants the executor to resolve the actual id at run time. We prefer a
// live lookup (most-recent tournament with a 4-Ball format) so this keeps
// working when the FULL DEMO is regenerated; PBCC_TEMPLATE_FALLBACK_ID is
// the snapshot id captured on 2026-06-23 — the original
// "4-Ball Championship — FULL DEMO" — used only when the live lookup
// returns nothing.
export const PBCC_TEMPLATE_SENTINEL = 'PBCC_TEMPLATE'
// PBCC build templates — selected by qualified-team count.
//   ≤ 96 teams → 12-flight layout
//   >  96 teams → 13-flight layout
const TEMPLATE_PATH_12 = 'templates/pbcc-4-ball-ratliff'
const TEMPLATE_PATH_13 = 'templates/pbcc-4-ball-championship-ratliff13'
// Size-aware good-template picker. Exported so the demo launcher (tab) and
// the floating AI (circle) resolve the SAME size-correct template the
// qualifying build uses — 96/12-flight → ratliff, 104/13-flight → ratliff13.
export const pickTemplatePath = (numTeams) => (numTeams > 96 ? TEMPLATE_PATH_13 : TEMPLATE_PATH_12)

// Resolve to the best available 4-Ball template.
//
//   1. First check /templates — the permanent template store. Director-
//      curated, never auto-cleaned. Pick the most-complete 4-Ball entry.
//   2. Fall back to scanning /tournaments for a 4-Ball tournament that
//      happens to have usable data (flights with teams + ideally
//      teeTimes). This kicks in only when no permanent template exists.
//   3. Return null if neither path turns up anything usable.
//
// Returns { id, name, teamCount, hasTeeTimes, source: 'template'|'live' }
// (source==='template' means id is a /templates/{slug} path id like
// 'templates/pbcc-4-ball'; the COPY handler treats those specially).
async function resolvePbccTemplate(db) {
  // 1) Permanent template store. Templates can live at the top level
  //    (templates/pbcc-4-ball) OR one level deep (templates/demos/pbcc-4-ball)
  //    so the Full Tournament Demo can park its auto-built copy in a
  //    grouped folder without colliding with user-saved templates. Walk
  //    one level of nesting: any node with its own `info` is a template;
  //    otherwise treat its children as templates.
  const templatesRaw = (await get(tref('templates'))).val() || {}
  const flatTemplates = []
  for (const [topSlug, top] of Object.entries(templatesRaw)) {
    if (!top || typeof top !== 'object') continue
    if (top.info) {
      flatTemplates.push({ slug: topSlug, t: top })
    } else {
      for (const [childSlug, child] of Object.entries(top)) {
        if (child && typeof child === 'object' && child.info) {
          flatTemplates.push({ slug: `${topSlug}/${childSlug}`, t: child })
        }
      }
    }
  }
  const templateMatches = flatTemplates
    .filter(({ t }) => {
      const fmt = String(t?.info?.format || '').toLowerCase()
      return fmt.includes('4-ball') || fmt.includes('4 ball')
          || fmt.includes('fourball') || fmt.includes('four-ball')
    })
    .map((m) => {
      const t = m.t
      const flights = t?.flights || {}
      const teamCount = Object.values(flights)
        .reduce((n, f) => n + (f && typeof f === 'object' && f.teams ? Object.keys(f.teams).length : 0), 0)
      const hasTeeTimes = !!(t?.teeTimes && Object.keys(t.teeTimes).length)
      const hasContests = !!t?.contests
      const hasRegistrations = !!t?.registrations
      const completeness =
        (teamCount > 0 ? 8 : 0) +
        (hasTeeTimes ? 4 : 0) +
        (hasContests ? 1 : 0) +
        (hasRegistrations ? 1 : 0)
      return { ...m, teamCount, hasTeeTimes, completeness, savedAt: t?.meta?.savedAt || 0 }
    })
    .filter((m) => m.teamCount > 0)
    .sort((a, b) => (b.completeness - a.completeness) || (b.savedAt - a.savedAt))
  if (templateMatches.length > 0) {
    const best = templateMatches[0]
    const resolvedPath = `templates/${best.slug}`
    // eslint-disable-next-line no-console
    console.log(
      `[AI] resolvePbccTemplate → /${resolvedPath} ` +
      `(name="${best.t?.meta?.templateName || best.t?.info?.name || best.slug}", ` +
      `teams=${best.teamCount}, teeTimes=${best.hasTeeTimes}, ` +
      `completeness=${best.completeness}, candidates=${templateMatches.length})`
    )
    return {
      id: resolvedPath,
      name: best.t?.meta?.templateName || best.t?.info?.name || best.slug,
      teamCount: best.teamCount,
      hasTeeTimes: best.hasTeeTimes,
      source: 'template',
    }
  }
  // eslint-disable-next-line no-console
  console.log(`[AI] resolvePbccTemplate → no template match, falling back to /tournaments scan`)

  // 2) Live /tournaments fallback (the old behavior — kept so existing
  //    tournaments-as-templates still work until Mark saves a real one).
  const all = (await get(tref('tournaments'))).val() || {}
  const matches = Object.entries(all)
    .map(([id, t]) => ({ id, t }))
    .filter(({ t }) => {
      const fmt = String(t?.info?.format || '').toLowerCase()
      return fmt.includes('4-ball') || fmt.includes('4 ball')
          || fmt.includes('fourball') || fmt.includes('four-ball')
    })
    .map((m) => {
      const t = m.t
      const flights = t?.flights || {}
      const teamCount = Object.values(flights)
        .reduce((n, f) => n + (f && typeof f === 'object' && f.teams ? Object.keys(f.teams).length : 0), 0)
      const hasTeeTimes = !!(t?.teeTimes && Object.keys(t.teeTimes).length)
      const hasContests = !!t?.contests
      const hasRegistrations = !!t?.registrations
      const completeness =
        (teamCount > 0 ? 8 : 0) +
        (hasTeeTimes ? 4 : 0) +
        (hasContests ? 1 : 0) +
        (hasRegistrations ? 1 : 0)
      return { ...m, teamCount, hasTeeTimes, completeness, createdAt: t?.info?.createdAt || 0 }
    })
    .filter((m) => m.teamCount > 0)
    .sort((a, b) => (b.completeness - a.completeness) || (b.createdAt - a.createdAt))
  if (matches.length === 0) return null
  const best = matches[0]
  return {
    id: best.id,
    name: best.t?.info?.name || '4-Ball template',
    teamCount: best.teamCount,
    hasTeeTimes: best.hasTeeTimes,
    source: 'live',
  }
}

const ACTION_HANDLERS = {
  MOVE_PLAYER:      handleMovePlayer,
  SWAP_GROUPS:      handleSwapGroups,
  MARK_NO_SHOW:     handleMarkNoShow,
  ADD_BLIND_DRAW:   handleAddBlindDraw,
  ADJUST_HANDICAP:  handleAdjustHandicap,
  DELAY_TEE_TIMES:  handleDelayTeeTimes,
  ADD_PLAYER:       handleAddPlayer,
  REMOVE_PLAYER:    handleRemovePlayer,
  CHANGE_TEE:       handleChangeTee,
  ENABLE_SCORING:   handleEnableScoring,
  // ── New tournament setup ─────────────────────────────────────────────
  CREATE_TOURNAMENT:              handleCreateTournament,
  BUILD_FLIGHTS:                  handleBuildFlights,
  GENERATE_TEE_TIMES:             handleGenerateTeeTimes,
  SETUP_COMPLETE_TOURNAMENT:      handleSetupCompleteTournament,
  COPY_TOURNAMENT:                handleCopyTournament,
  // ── Calcutta ─────────────────────────────────────────────────────────
  OPEN_CALCUTTA:                  handleOpenCalcutta,
  CLOSE_CALCUTTA:                 handleCloseCalcutta,
  CALCUTTA_STANDINGS:             handleReadOnlyNoop,
  // ── Player management ────────────────────────────────────────────────
  IMPORT_LAST_TOURNAMENT_PLAYERS: handleImportLastTournamentPlayers,
  AUTO_ASSIGN_TEAMS:              handleAutoAssignTeams,
  // ── Post-round ───────────────────────────────────────────────────────
  FINALIZE_ROUND:                 handleFinalizeRound,
  CALCULATE_PAYOUTS:              handleReadOnlyNoop,
  // ── Communications ───────────────────────────────────────────────────
  NOTIFY_DELAY:                   handleNotifyDelay,
  POST_ANNOUNCEMENT:              handlePostAnnouncement,
  // ── Scoring ──────────────────────────────────────────────────────────
  RESET_SCORES:                   handleResetScores,
  // ── Scores (manual enter/edit — not just reset) ─────────────────────
  SET_QUALIFYING_SCORE:           handleSetQualifyingScore,
  SET_LIVE_SCORE:                 handleSetLiveScore,
  // ── Brackets ────────────────────────────────────────────────────────
  SET_MATCH_WINNER:               handleSetMatchWinner,
  // ── Flights & matchups (whole team + pairing changes) ───────────────
  MOVE_TEAM:                      handleMoveTeam,
  SET_MATCHUP:                    handleSetMatchup,
  // ── Registration (rename / add / remove a team) ─────────────────────
  RENAME_TEAM:                    handleRenameTeam,
  ADD_REGISTRATION:               handleAddRegistration,
  REMOVE_REGISTRATION:            handleRemoveRegistration,
  // ── PBCC-specific ───────────────────────────────────────────────────
  PBCC_BUILD_FROM_QUALIFYING:     handlePbccBuildFromQualifying,
  // ── Read-only club lookups (handicap & courses) ─────────────────────
  PLAYER_HANDICAP_LOOKUP:         handlePlayerHandicapLookup,
  COURSE_LOOKUP:                  handleCourseLookup,
  GHIN_LOOKUP:                    handleGhinLookup,
  // ── GHIN / handicap system (AI GHIN completion) ─────────────────────
  PULL_GHINS_FOR_FIELD:           handlePullGhinsForField,
  SET_EVENT_ALLOWANCE:            handleSetEventAllowance,
  SET_PLAYER_ALLOWANCE:           handleSetPlayerAllowance,
  SET_SELF_REPORTED_GATE:         handleSetSelfReportedGate,
  SET_TV_NET_MODE:                handleSetTvNetMode,
  HANDICAP_REVIEW:                handleHandicapReview,
}

export async function executeAIAction(action, params, tournamentId, db, command = '') {
  const handler = ACTION_HANDLERS[action]
  let result = { success: false, message: `Unknown action: ${action}` }
  if (handler) {
    try {
      result = await handler(params || {}, tournamentId, db)
    } catch (err) {
      result = { success: false, message: err?.message || String(err) }
    }
  }
  // Best-effort audit log. Don't fail the action if logging fails.
  // Skip when there's no tournamentId (dashboard-mounted AI panel) —
  // otherwise the template literal becomes 'tournaments/null/aiLog' and
  // RTDB creates a phantom /tournaments/null entry that shows up in the
  // admin list as a Course-TBD shell.
  if (tournamentId) {
    try {
      const logRef = push(tref(`tournaments/${tournamentId}/aiLog`))
      await update(logRef, {
        command,
        action,
        params,
        executed: !!result.success,
        result: result.message || '',
        timestamp: serverTimestamp(),
      })
    } catch {}
  }
  return result
}

// Single source of truth for the demo "Set Up with AI" build. BOTH the demo
// launcher TAB and the floating AI CIRCLE call this, so they can never
// diverge — same template selection, same COPY path, identical result for a
// given roster. 4-Ball clones the SIZE-CORRECT good template (96 →
// ratliff/12-flight, 104 → ratliff13/13-flight; each carries the 3-day
// consolation bracket + 8am/1pm flip tee times); stroke play clones its own
// template. handleCopyTournament strips isDemo/isTestStation → REAL tournament.
export async function buildDemoTournament(
  db,
  { mode = 'fullTournament', size = 96, name, date, tournamentId = null, practice = false } = {},
) {
  // Teams-only demos (no qualifying): scramble/stroke × field/flight. Built
  // through the SAME AI builder as every other demo so they all land in the
  // tournament center the same way. createDemoTournament writes the full
  // tournament (info format, flights+teams, shotgun tee times) — single-flight
  // defs = vs FIELD, multi-flight defs = vs FLIGHT. Caller navigates to /admin/t.
  if (['scramble', 'scrambleFlight', 'bestBallField', 'bestBallFlight', 'shamble', 'alternateShot', 'stableford', 'quota', 'skins'].includes(mode)) {
    const r = await createDemoTournament(mode)
    // Best Ball tee sheet: use the 4-Ball's EXACT method (buildWaveGroups pairs
    // 2 teams per group, assignWaveHoles spreads 1 group per hole across all 18 —
    // 2 teams / 4 players per hole, no A/B skipping). Scramble already bakes a
    // 1-team-per-hole startingHole and renders via the ScrambleShotgun panel.
    // Non-scramble teams-only demos (Best Ball, Shamble, Alt Shot, Stableford,
    // Quota, Skins) render via the ShotgunDay tee sheet → assign 2 teams/hole.
    // Scramble bakes its own 1-team-per-hole startingHole, so skip it there.
    if (mode !== 'scramble' && mode !== 'scrambleFlight') {
      await assignShotgunHoles(r.tournamentId, { startTime: '8:00 AM' })
    }
    return { success: true, tournamentId: r.tournamentId, message: 'Demo built.' }
  }
  const isStrokePlay = mode === 'strokePlay'
  // 4-Ball at a size other than the two pre-baked templates (96/104), OR any
  // practice build: create a demo shell, seed the made-up field, and run the
  // standard PBCC build so it produces the right number of 8-team flights with
  // brackets — flagged Demo (and Practice when asked).
  if (!isStrokePlay && (practice || (size !== 96 && size !== 104))) {
    const flights = Math.max(1, Math.min(13, Math.round(size / 8)))
    const info = {
      name: name || `PBCC 4-Ball ${practice ? 'Practice' : 'Demo'} — ${flights} flight${flights === 1 ? '' : 's'}`,
      format: '4-Ball Best Ball',
      course: 'Pine Bluff Country Club',
      date: date || new Date().toISOString().slice(0, 10),
      isDemo: true,
      demoCreatedAt: Date.now(),
    }
    if (practice) info.practice = true
    const id = tournamentId || (await createTournament(info))
    await bulkAddRegistrations(id, demoRegistrationRows(flights))
    const result = await executeAIAction('PBCC_BUILD_FROM_QUALIFYING', {}, id, db)
    return { ...result, tournamentId: id }
  }
  const sourceTournamentId = isStrokePlay
    ? 'templates/demos/stroke-play-36'
    : pickTemplatePath(size)
  return executeAIAction('COPY_TOURNAMENT', {
    sourceTournamentId,
    newName: name || (isStrokePlay ? 'PBCC Stroke Play Demo' : 'PBCC 4-Ball Championship'),
    newDate: date || new Date().toISOString().slice(0, 10),
  }, tournamentId, db)
}

// ─── MOVE_PLAYER ────────────────────────────────────────────────────────────
async function handleMovePlayer(p, tournamentId, db) {
  const { playerName, fromFlightId, fromTeamId, toFlightId, toTeamId } = p
  if (!playerName || !fromFlightId || !fromTeamId || !toFlightId || !toTeamId) {
    return { success: false, message: 'Move requires playerName + from/to flight + team.' }
  }
  const fromInfo = await readInfo(db, tournamentId, fromFlightId, fromTeamId)
  const toInfo   = await readInfo(db, tournamentId, toFlightId, toTeamId)
  if (!fromInfo) return { success: false, message: `Source team not found (${fromFlightId}/${fromTeamId}).` }
  if (!toInfo)   return { success: false, message: `Destination team not found (${toFlightId}/${toTeamId}).` }

  const fromList = Array.isArray(fromInfo.players) ? [...fromInfo.players] : []
  const idx = findPlayerIndex(fromList, playerName)
  if (idx < 0) return { success: false, message: `${playerName} not found on source team.` }
  const moved = fromList[idx]
  fromList.splice(idx, 1)
  const toList = Array.isArray(toInfo.players) ? [...toInfo.players, moved] : [moved]

  const updates = {
    [`tournaments/${tournamentId}/flights/${fromFlightId}/teams/${fromTeamId}/info/players`]: fromList,
    [`tournaments/${tournamentId}/flights/${toFlightId}/teams/${toTeamId}/info/players`]: toList,
  }
  await updateTenant(updates)
  return { success: true, message: `Moved ${playerName} to ${toInfo.name || 'destination team'}.` }
}

// ─── SWAP_GROUPS ────────────────────────────────────────────────────────────
//
// Swap the tee-time strings (or wave start times when present) for two
// flights on a given day. Works against the simple per-flight model:
//   teeTimes/{day}/{flightId} = "8:00 AM"
async function handleSwapGroups(p, tournamentId, db) {
  const { flight1Id, flight2Id, day } = p
  if (!flight1Id || !flight2Id || !day) {
    return { success: false, message: 'Swap requires flight1Id, flight2Id, and day.' }
  }
  const snap = await get(tref(`tournaments/${tournamentId}/teeTimes/${day}`))
  const dayData = snap.val() || {}
  const t1 = dayData[flight1Id]
  const t2 = dayData[flight2Id]
  if (t1 == null && t2 == null) {
    return { success: false, message: `Neither flight has a tee time on day ${day}.` }
  }
  const updates = {
    [`tournaments/${tournamentId}/teeTimes/${day}/${flight1Id}`]: t2 ?? null,
    [`tournaments/${tournamentId}/teeTimes/${day}/${flight2Id}`]: t1 ?? null,
  }
  await updateTenant(updates)
  return { success: true, message: `Swapped Day ${day} tee times between the two flights.` }
}

// ─── MARK_NO_SHOW ───────────────────────────────────────────────────────────
//
// players[] is an array of strings in the live shape. Replace the matched
// entry with an object { name, noShow:true } and write back. If addBlindDraw
// is set, append a Blind Draw placeholder on the same team.
async function handleMarkNoShow(p, tournamentId, db) {
  const { playerName, flightId, teamId, addBlindDraw } = p
  if (!playerName || !flightId || !teamId) {
    return { success: false, message: 'Mark no-show requires playerName + flightId + teamId.' }
  }
  const info = await readInfo(db, tournamentId, flightId, teamId)
  if (!info) return { success: false, message: `Team not found (${flightId}/${teamId}).` }
  const list = Array.isArray(info.players) ? [...info.players] : []
  const idx = findPlayerIndex(list, playerName)
  if (idx < 0) return { success: false, message: `${playerName} not found on that team.` }
  const existing = list[idx]
  const baseName = typeof existing === 'string' ? existing : (existing?.name || playerName)
  list[idx] = typeof existing === 'string'
    ? { name: baseName, noShow: true }
    : { ...existing, noShow: true }
  if (addBlindDraw) {
    list.push({ name: 'Blind Draw', isBlindDraw: true })
  }
  await update(tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/info`), {
    players: list,
  })
  return {
    success: true,
    message: addBlindDraw
      ? `Marked ${baseName} as a no-show and added a Blind Draw.`
      : `Marked ${baseName} as a no-show.`,
  }
}

// ─── ADD_BLIND_DRAW ─────────────────────────────────────────────────────────
async function handleAddBlindDraw(p, tournamentId, db) {
  const { flightId, teamId } = p
  if (!flightId || !teamId) {
    return { success: false, message: 'Add blind draw requires flightId + teamId.' }
  }
  const info = await readInfo(db, tournamentId, flightId, teamId)
  if (!info) return { success: false, message: `Team not found (${flightId}/${teamId}).` }
  const list = Array.isArray(info.players) ? [...info.players] : []
  list.push({ name: 'Blind Draw', isBlindDraw: true })
  await update(tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/info`), {
    players: list,
  })
  return { success: true, message: `Blind Draw added to ${info.name || 'team'}.` }
}

// ─── ADJUST_HANDICAP ────────────────────────────────────────────────────────
async function handleAdjustHandicap(p, tournamentId, db) {
  const { playerName, newHandicap } = p
  if (!playerName || newHandicap == null || !Number.isFinite(Number(newHandicap))) {
    return { success: false, message: 'Adjust handicap requires playerName + newHandicap.' }
  }
  const playersSnap = await get(tref(`tournaments/${tournamentId}/players`))
  const players = playersSnap.val() || {}
  const pid = findPlayerIdByName(players, playerName)
  if (!pid) return { success: false, message: `No player matching "${playerName}" in the roster.` }
  await update(tref(`tournaments/${tournamentId}/players/${pid}`), {
    handicap: Number(newHandicap),
  })
  return { success: true, message: `Set ${players[pid].name}'s handicap to ${newHandicap}.` }
}

// ─── DELAY_TEE_TIMES ────────────────────────────────────────────────────────
//
// Shift every tee time on the given day by delayMinutes. Handles BOTH the
// simple per-flight string model and the shotgun wave startTime model.
async function handleDelayTeeTimes(p, tournamentId, db) {
  const { delayMinutes, day } = p
  if (!Number.isFinite(Number(delayMinutes)) || !day) {
    return { success: false, message: 'Delay tee times requires delayMinutes + day.' }
  }
  const mins = Number(delayMinutes)
  const snap = await get(tref(`tournaments/${tournamentId}/teeTimes/${day}`))
  const dayData = snap.val() || {}
  const updates = {}
  let touched = 0
  for (const [k, v] of Object.entries(dayData)) {
    if (k === 'waves') continue
    if (typeof v === 'string') {
      const shifted = shiftTimeString(v, mins)
      if (shifted) {
        updates[`tournaments/${tournamentId}/teeTimes/${day}/${k}`] = shifted
        touched++
      }
    }
  }
  const waves = dayData.waves || {}
  for (const [waveKey, wave] of Object.entries(waves)) {
    if (wave?.startTime && typeof wave.startTime === 'string') {
      const shifted = shiftTimeString(wave.startTime, mins)
      if (shifted) {
        updates[`tournaments/${tournamentId}/teeTimes/${day}/waves/${waveKey}/startTime`] = shifted
        touched++
      }
    }
  }
  if (touched === 0) {
    return { success: false, message: `No tee times found on day ${day} to shift.` }
  }
  await updateTenant(updates)
  const sign = mins >= 0 ? 'Delayed' : 'Advanced'
  return { success: true, message: `${sign} ${touched} tee time${touched === 1 ? '' : 's'} on Day ${day} by ${Math.abs(mins)} min.` }
}

// ─── ADD_PLAYER ─────────────────────────────────────────────────────────────
async function handleAddPlayer(p, tournamentId, db) {
  const { playerName, flightId, teamId, handicap } = p
  if (!playerName || !flightId || !teamId) {
    return { success: false, message: 'Add player requires playerName + flightId + teamId.' }
  }
  const info = await readInfo(db, tournamentId, flightId, teamId)
  if (!info) return { success: false, message: `Team not found (${flightId}/${teamId}).` }
  const list = Array.isArray(info.players) ? [...info.players] : []
  list.push(playerName)
  await update(tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/info`), {
    players: list,
  })
  // Optional: also seed a roster entry so handicap can be referenced later.
  if (handicap != null && Number.isFinite(Number(handicap))) {
    const newPlayerRef = push(tref(`tournaments/${tournamentId}/players`))
    await update(newPlayerRef, { name: playerName, handicap: Number(handicap) })
  }
  return { success: true, message: `Added ${playerName} to ${info.name || 'team'}.` }
}

// ─── REMOVE_PLAYER ──────────────────────────────────────────────────────────
async function handleRemovePlayer(p, tournamentId, db) {
  const { playerName, flightId, teamId } = p
  if (!playerName || !flightId || !teamId) {
    return { success: false, message: 'Remove player requires playerName + flightId + teamId.' }
  }
  const info = await readInfo(db, tournamentId, flightId, teamId)
  if (!info) return { success: false, message: `Team not found (${flightId}/${teamId}).` }
  const list = Array.isArray(info.players) ? [...info.players] : []
  const idx = findPlayerIndex(list, playerName)
  if (idx < 0) return { success: false, message: `${playerName} not found on that team.` }
  const removed = list[idx]
  const removedName = typeof removed === 'string' ? removed : (removed?.name || playerName)
  list.splice(idx, 1)
  await update(tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/info`), {
    players: list,
  })
  return { success: true, message: `Removed ${removedName} from ${info.name || 'team'}.` }
}

// ─── CHANGE_TEE ─────────────────────────────────────────────────────────────
async function handleChangeTee(p, tournamentId, db) {
  const { flightId, teamId, newStartingHole } = p
  if (!flightId || !teamId || !newStartingHole) {
    return { success: false, message: 'Change tee requires flightId + teamId + newStartingHole.' }
  }
  const hole = Number(newStartingHole)
  if (!Number.isFinite(hole) || hole < 1 || hole > 18) {
    return { success: false, message: `Starting hole must be 1–18 (got ${newStartingHole}).` }
  }
  await update(tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/info`), {
    startingHole: hole,
  })
  return { success: true, message: `Starting hole set to ${hole}.` }
}

// ─── ENABLE_SCORING ─────────────────────────────────────────────────────────
async function handleEnableScoring(p, tournamentId, db) {
  const { enabled } = p
  if (typeof enabled !== 'boolean') {
    return { success: false, message: 'Enable scoring requires a boolean.' }
  }
  await update(tref(`tournaments/${tournamentId}/info`), {
    scoringEnabled: enabled,
  })
  return { success: true, message: enabled ? 'Scoring is now OPEN.' : 'Scoring is now LOCKED.' }
}

// ─── CREATE_TOURNAMENT ──────────────────────────────────────────────────────
//
// Build a fresh tournament tree mirroring src/lib/data.js + demos.js shape.
// Auto-flights: 1 flight unless numTeams >= 24 — then ceil(numTeams/24) of ≤24.
// Shotgun start seeds teeTimes/1/waves/wave1; tee-times start seeds the simple
// per-flight string. Returns the new tournament id in the message so the
// director can navigate to it.
async function handleCreateTournament(p, _tournamentId, db) {
  const {
    name, date, format, numTeams, numPlayers, startTime,
    startType, holesPerRound,
  } = p
  if (!name) return { success: false, message: 'Create tournament requires a name.' }

  // 4-Ball short-circuit — runs BEFORE any /tournaments write. Two
  // critical invariants:
  //
  //   1. handleCreateTournament does NOT push or set anything for 4-Ball
  //      formats. The shell-build path below is unreachable in that case.
  //      All persistence goes through handleCopyTournament's atomic
  //      multi-path update().
  //
  //   2. NO FALLTHROUGH. Whether the template exists or not, whether the
  //      COPY succeeds or fails, this branch always returns. The earlier
  //      "console.warn + fall through to fresh build" path produced the
  //      double-tournament-creation bug: a failed-verify COPY left one
  //      tournament behind AND the shell build wrote a second one.
  const fmtLower = String(format || '').toLowerCase()
  const isFourBall = fmtLower.includes('4-ball') || fmtLower.includes('4 ball')
                  || fmtLower.includes('fourball') || fmtLower.includes('four-ball')
  if (isFourBall) {
    const resolved = await resolvePbccTemplate(db)
    if (!resolved || resolved.source !== 'template' || !(resolved.teamCount > 0)) {
      // eslint-disable-next-line no-console
      console.warn('[AI] handleCreateTournament → 4-Ball requested but no usable template in /templates')
      return {
        success: false,
        message:
          'No PBCC 4-Ball template found in /templates. ' +
          'Save a 4-Ball tournament as a template first ' +
          '(or click Try a Demo → Full Tournament Demo to seed one), then try again. ' +
          'I will not create an empty 4-Ball shell.',
      }
    }
    // eslint-disable-next-line no-console
    console.log(
      `[AI] handleCreateTournament → diverting to COPY from /${resolved.id} ` +
      `(template "${resolved.name}", ${resolved.teamCount} teams)`
    )
    const copyResult = await handleCopyTournament(
      { sourceTournamentId: resolved.id, newDate: date, newName: name },
      _tournamentId, db,
    )
    if (copyResult?.success) {
      return {
        ...copyResult,
        message: `Created "${name}" from template /${resolved.id} — ${copyResult.message.replace(/^Copied[^:]+:\s*/, '')}`,
      }
    }
    // COPY failed — return the failure verbatim. Do NOT fall through to
    // build a shell; that's what was creating the second tournament.
    return copyResult || {
      success: false,
      message: '4-Ball template COPY failed with no message.',
    }
  }

  // Non-4-Ball path below — original shell-build behavior, unchanged.
  const totalTeams = Math.max(1, Number(numTeams) || 0)
  const playersPerTeam = Math.max(1, Math.round((Number(numPlayers) || totalTeams * 4) / Math.max(1, totalTeams)))
  const numFlights = totalTeams >= 24 ? Math.ceil(totalTeams / 24) : 1
  const teamsPerFlight = Math.ceil(totalTeams / numFlights)

  const newRef = push(tref('tournaments'))
  const newId = newRef.key
  const flights = {}
  let placed = 0
  for (let f = 0; f < numFlights; f++) {
    const flightId = numFlights === 1 ? 'flightA' : `flight-${String(f + 1).padStart(2, '0')}`
    const fmtName = format || '4-Ball Best Ball'
    const teams = {}
    const teamsThisFlight = Math.min(teamsPerFlight, totalTeams - placed)
    for (let t = 0; t < teamsThisFlight; t++) {
      const teamId = `team-${String(placed + 1).padStart(2, '0')}`
      teams[teamId] = {
        info: {
          name: `Team ${placed + 1}`,
          players: Array.from({ length: playersPerTeam }, () => ''),
          startingHole: startType === 'shotgun' ? ((placed % 18) + 1) : 1,
        },
      }
      placed++
    }
    flights[flightId] = {
      info: {
        name: numFlights === 1 ? 'Main Flight' : `Flight ${f + 1}`,
        format: fmtName,
      },
      teams,
    }
  }

  const tree = {
    info: {
      name,
      course: CLUB.course?.name || '',
      date: date || new Date().toISOString().slice(0, 10),
      format: format || '4-Ball Best Ball',
      startType: startType || 'tee_times',
      teeStyle: startType === 'shotgun' ? 'Shotgun start' : 'Tee times',
      holesPerRound: Number(holesPerRound) || 18,
      status: 'open',
      createdAt: Date.now(),
    },
    flights,
  }
  try { ensureTournamentInfo(tree.info) }
  catch (e) { return { success: false, message: e.message } }

  const teeTimeStr = normalizeTimeString(startTime) || '8:00 AM'
  if (startType === 'shotgun') {
    const flightIds = Object.keys(flights)
    const flightsMap = {}
    for (const fid of flightIds) flightsMap[fid] = true
    tree.teeTimes = {
      1: { waves: { wave1: { startTime: teeTimeStr, flights: flightsMap, assignments: {} } } },
    }
  } else {
    const dayTimes = {}
    for (const fid of Object.keys(flights)) dayTimes[fid] = teeTimeStr
    tree.teeTimes = { 1: dayTimes }
  }

  await set(newRef, tree)
  return {
    success: true,
    message: `Created tournament "${name}" (id ${newId}) with ${numFlights} flight${numFlights === 1 ? '' : 's'} and ${totalTeams} team${totalTeams === 1 ? '' : 's'}.`,
    tournamentId: newId,
  }
}

// ─── BUILD_FLIGHTS ──────────────────────────────────────────────────────────
//
// Reads the existing roster and creates flights with seeded team slots inside
// the active tournament. For 'handicap', sorts roster ascending by handicap
// and assigns sequentially to team slots; for 'random', shuffles; for
// 'manual', creates empty flights without populating players.
async function handleBuildFlights(p, tournamentId, db) {
  const { numFlights, playersPerFlight, method } = p
  const N = Math.max(1, Number(numFlights) || 1)
  const perFlight = Math.max(1, Number(playersPerFlight) || 0)
  if (!perFlight) return { success: false, message: 'Build flights requires playersPerFlight.' }

  const playersSnap = await get(tref(`tournaments/${tournamentId}/players`))
  const playersMap = playersSnap.val() || {}
  let roster = Object.entries(playersMap).map(([pid, v]) => ({
    pid,
    name: String(v?.name || ''),
    handicap: Number(v?.handicap ?? v?.handicapIndex ?? 99),
  }))

  if (method === 'handicap') {
    roster.sort((a, b) => a.handicap - b.handicap)
  } else if (method === 'random') {
    for (let i = roster.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[roster[i], roster[j]] = [roster[j], roster[i]]
    }
  } // method === 'manual' → leave roster untouched, but we still create empty slots

  // Players per team = 2 by default (4-ball). Could be 1 for individual.
  const playersPerTeam = 2
  const teamsPerFlight = Math.max(1, Math.floor(perFlight / playersPerTeam))

  const updates = {}
  let cursor = 0
  for (let f = 0; f < N; f++) {
    const flightId = `flight-${String(f + 1).padStart(2, '0')}`
    updates[`tournaments/${tournamentId}/flights/${flightId}/info`] = {
      name: `Flight ${f + 1}`,
    }
    for (let t = 0; t < teamsPerFlight; t++) {
      const teamId = `team-${String(t + 1).padStart(2, '0')}`
      const playerSlots = []
      for (let s = 0; s < playersPerTeam; s++) {
        if (method !== 'manual' && cursor < roster.length) {
          playerSlots.push(roster[cursor].name)
          cursor++
        } else {
          playerSlots.push('')
        }
      }
      updates[`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/info`] = {
        name: playerSlots.filter(Boolean).map((n) => n.split(/\s+/).pop()).join('/') || `Team ${t + 1}`,
        players: playerSlots,
        startingHole: 1,
      }
    }
  }
  await updateTenant(updates)
  return {
    success: true,
    message: `Built ${N} flight${N === 1 ? '' : 's'} of ${teamsPerFlight} team${teamsPerFlight === 1 ? '' : 's'} (${method || 'manual'} assignment).`,
  }
}

// ─── GENERATE_TEE_TIMES ─────────────────────────────────────────────────────
//
// For 'sequential', walk every flight on day 1 and step startTime by interval.
// For 'shotgun', drop all flights into waves/wave1 and assign each team a
// startingHole cycling 1..18.
async function handleGenerateTeeTimes(p, tournamentId, db) {
  const { startTime, intervalMinutes, type } = p
  const baseTime = normalizeTimeString(startTime)
  if (!baseTime) return { success: false, message: `Could not parse start time "${startTime}".` }
  const interval = Math.max(0, Number(intervalMinutes) || 0)

  const flightsSnap = await get(tref(`tournaments/${tournamentId}/flights`))
  const flights = flightsSnap.val() || {}
  const flightIds = Object.keys(flights)
  if (flightIds.length === 0) {
    return { success: false, message: 'No flights exist on this tournament yet.' }
  }

  if (type === 'shotgun') {
    const flightsMap = {}
    for (const fid of flightIds) flightsMap[fid] = true
    const updates = {
      [`tournaments/${tournamentId}/teeTimes/1/waves/wave1/startTime`]: baseTime,
      [`tournaments/${tournamentId}/teeTimes/1/waves/wave1/flights`]: flightsMap,
    }
    // Cycle starting holes 1..18 across all teams in flight order.
    let i = 0
    for (const fid of flightIds) {
      const teams = flights[fid]?.teams || {}
      for (const tid of Object.keys(teams)) {
        const hole = (i % 18) + 1
        updates[`tournaments/${tournamentId}/flights/${fid}/teams/${tid}/info/startingHole`] = hole
        i++
      }
    }
    await updateTenant(updates)
    return { success: true, message: `Shotgun start set to ${baseTime} with ${i} team${i === 1 ? '' : 's'} assigned across 18 holes.` }
  }

  // sequential
  const updates = {}
  flightIds.forEach((fid, idx) => {
    const shifted = shiftTimeString(baseTime, idx * interval)
    if (shifted) updates[`tournaments/${tournamentId}/teeTimes/1/${fid}`] = shifted
  })
  await updateTenant(updates)
  return { success: true, message: `Set ${flightIds.length} sequential tee times starting ${baseTime} every ${interval} min.` }
}

// ─── COPY_TOURNAMENT ────────────────────────────────────────────────────────
async function handleCopyTournament(p, _tournamentId, db) {
  let { sourceTournamentId, newDate, newName } = p
  // Resolve the PBCC template sentinel (or missing source) at run time so
  // the AI never has to know the actual tournament id.
  if (sourceTournamentId === PBCC_TEMPLATE_SENTINEL || !sourceTournamentId) {
    const resolved = await resolvePbccTemplate(db)
    if (!resolved) {
      return { success: false, message: 'No PBCC 4-Ball template found. Run "Try a Demo → Full Tournament Demo" once, then try again.' }
    }
    sourceTournamentId = resolved.id
  }
  // Read each known top-level node separately. Per-node reads make missing
  // data obvious in the diagnostic message and side-step any weirdness with
  // one giant tree read on tournaments with lots of children.
  //
  //   Copied verbatim:  info (with override), flights, players, teeTimes,
  //                     contests, qualifying, registrations, photoSettings,
  //                     photoAdmins, tvSettings, brackets
  //   Calcutta:         only `settings`, with auctionOpen forced false
  //   Stripped per team: scores
  //   Never copied:     scores, results, aiLog, announcements, tracking,
  //                     photos, calcutta/bids+winners+owners
  const readNode = async (path) => (await get(tref(path))).val()
  // Source can be either a live tournament id OR a path-prefixed template
  // id like 'templates/pbcc-4-ball' (set by the resolver when the AI is
  // pulling from the permanent /templates store). Both paths have the
  // same internal shape — info, flights, teeTimes, etc. — so we just
  // compute the base path once.
  const sid = sourceTournamentId
  const basePath = sid.startsWith('templates/')
    ? sid
    : `tournaments/${sid}`
  // eslint-disable-next-line no-console
  console.log(`[AI] handleCopyTournament → deep-copying from /${basePath}`)
  const [
    srcInfo, srcFlights, srcPlayers, srcTeeTimes, srcContests, srcCalcutta,
    srcQualifying, srcRegistrations, srcPhotoSettings, srcPhotoAdmins,
    srcTvSettings, srcBrackets,
  ] = await Promise.all([
    readNode(`${basePath}/info`),
    readNode(`${basePath}/flights`),
    readNode(`${basePath}/players`),
    readNode(`${basePath}/teeTimes`),
    readNode(`${basePath}/contests`),
    readNode(`${basePath}/calcutta`),
    readNode(`${basePath}/qualifying`),
    readNode(`${basePath}/registrations`),
    readNode(`${basePath}/photoSettings`),
    readNode(`${basePath}/photoAdmins`),
    readNode(`${basePath}/tvSettings`),
    readNode(`${basePath}/brackets`),
  ])
  if (!srcInfo) {
    return { success: false, message: `Source tournament ${sid} has no info node.` }
  }
  if (!srcFlights) {
    return { success: false, message: `Source tournament ${sid} has no flights — nothing to copy.` }
  }

  // Strip per-team scores; deep-clone first so we don't mutate the source-
  // in-memory copy (it's harmless here but is a good habit).
  //
  // CRITICAL: the loop below ONLY deletes `scores`. It MUST NOT touch
  // `info.startingHole`, `info.opponentId`, `info.players`, or any other
  // field on `info`. Template tee assignments come over byte-for-byte.
  const flightsCopy = JSON.parse(JSON.stringify(srcFlights))
  let sourceTeamCount = 0
  const sampleHoles = [] // [{tid, hole}] for post-write verification
  for (const fid of Object.keys(flightsCopy)) {
    const f = flightsCopy[fid]
    if (!f || typeof f !== 'object') continue
    if (f.teams && typeof f.teams === 'object') {
      sourceTeamCount += Object.keys(f.teams).length
      for (const tid of Object.keys(f.teams)) {
        if (f.teams[tid] && typeof f.teams[tid] === 'object' && f.teams[tid].scores) {
          delete f.teams[tid].scores
        }
        // Record the team's startingHole from the (already-cloned) source
        // so we can verify it lands in the destination unchanged.
        const srcHole = f.teams[tid]?.info?.startingHole
        if (sampleHoles.length < 3 && srcHole != null) {
          sampleHoles.push({ fid, tid, hole: srcHole })
        }
      }
    }
  }
  if (sourceTeamCount === 0) {
    return { success: false, message: `Source tournament ${sid} has flights but no teams inside them — useless as a template. Run "Try a Demo → Full Tournament Demo" to recreate a populated 4-Ball template.` }
  }

  // Snapshot what we expect to write so we can compare after the multi-path
  // update lands. The teeTimes node is written by reference (srcTeeTimes is
  // never mutated below), and we stringify it now so any post-write drift
  // shows up in the verification step.
  const srcTeeTimesJson = JSON.stringify(srcTeeTimes ?? null)
  const teeTimesDayCount = srcTeeTimes && typeof srcTeeTimes === 'object'
    ? Object.keys(srcTeeTimes).filter((k) => srcTeeTimes[k] != null).length
    : 0
  const wave1AssignmentCount = srcTeeTimes
    ? Object.values(srcTeeTimes)
        .filter((d) => d && typeof d === 'object')
        .reduce((n, d) => n + Object.keys(d?.waves?.wave1?.assignments || {}).length, 0)
    : 0
  // eslint-disable-next-line no-console
  console.log(
    `[AI] handleCopyTournament source snapshot: ${sourceTeamCount} teams · ` +
    `teeTimes days=${teeTimesDayCount} · wave1 assignments=${wave1AssignmentCount} · ` +
    `sample startingHoles=${JSON.stringify(sampleHoles)}`
  )

  // Calcutta — only settings (drop bids, owners, winners — fresh auction).
  const calcuttaCopy = srcCalcutta && srcCalcutta.settings
    ? { settings: { ...srcCalcutta.settings, auctionOpen: false } }
    : null

  const newRef = push(tref('tournaments'))
  const newId = newRef.key
  const newInfo = {
    ...srcInfo,
    name: newName || `${srcInfo.name || 'Tournament'} (Copy)`,
    course: srcInfo.course || CLUB.course?.name || '',
    date: newDate || new Date().toISOString().slice(0, 10),
    status: 'open',
    createdAt: Date.now(),
    closedAt: null,
  }
  // A COPY is always a REAL, director-owned tournament — never a demo/test
  // fixture. Strip any demo/test flags the source may carry so the new
  // tournament can't be swept by the 30-minute demo TTL or the Test Station
  // "Clean Up All Test Data" sweep. (Good templates are already clean; this
  // is the safety net that guarantees requirement even if a source isn't.)
  delete newInfo.isDemo
  delete newInfo.isTestStation
  delete newInfo.testStationTile
  delete newInfo.testStationCreatedAt
  delete newInfo.demoCreatedAt
  delete newInfo.practice
  try { ensureTournamentInfo(newInfo) }
  catch (e) { return { success: false, message: e.message, tournamentId: newId } }

  // Atomic multi-path update — every node lands or none of them do. Using
  // update({}) at root lets us write per-node paths in one transaction
  // rather than one big set() that's harder to reason about when a child
  // path silently fails a validator.
  const writes = {}
  writes[`tournaments/${newId}/info`] = newInfo
  writes[`tournaments/${newId}/flights`] = flightsCopy
  if (srcPlayers)         writes[`tournaments/${newId}/players`]        = srcPlayers
  if (srcTeeTimes)        writes[`tournaments/${newId}/teeTimes`]       = srcTeeTimes
  if (srcContests)        writes[`tournaments/${newId}/contests`]       = srcContests
  if (calcuttaCopy)       writes[`tournaments/${newId}/calcutta`]       = calcuttaCopy
  if (srcQualifying)      writes[`tournaments/${newId}/qualifying`]     = srcQualifying
  if (srcRegistrations)   writes[`tournaments/${newId}/registrations`]  = srcRegistrations
  if (srcPhotoSettings)   writes[`tournaments/${newId}/photoSettings`]  = srcPhotoSettings
  if (srcPhotoAdmins)     writes[`tournaments/${newId}/photoAdmins`]    = srcPhotoAdmins
  if (srcTvSettings)      writes[`tournaments/${newId}/tvSettings`]     = srcTvSettings
  if (srcBrackets)        writes[`tournaments/${newId}/brackets`]       = srcBrackets
  await updateTenant(writes)

  // Verify by reading the new tournament's flights + teeTimes directly.
  const [vFlights, vTeeTimes] = await Promise.all([
    readNode(`tournaments/${newId}/flights`),
    readNode(`tournaments/${newId}/teeTimes`),
  ])
  const flightCount = vFlights
    ? Object.keys(vFlights).filter((k) => !k.startsWith('_')).length
    : 0
  const writtenTeamCount = vFlights
    ? Object.values(vFlights).reduce((n, f) => n + (f && f.teams ? Object.keys(f.teams).length : 0), 0)
    : 0
  const teeTimesPresent = !!vTeeTimes

  if (flightCount === 0) {
    return {
      success: false,
      message: `Copy failed verification — source had ${Object.keys(srcFlights).length} flights/${sourceTeamCount} teams, but new tournament ${newId} read back ${flightCount} flights.`,
      tournamentId: newId,
    }
  }

  // Verify teeTimes round-tripped verbatim. Stringified comparison so any
  // byte-level drift (Firebase array coercion aside — that's deterministic
  // and matches the source's own coercion) is caught.
  const vTeeTimesJson = JSON.stringify(vTeeTimes ?? null)
  const teeTimesMatch = vTeeTimesJson === srcTeeTimesJson
  const vWave1AssignmentCount = vTeeTimes
    ? Object.values(vTeeTimes)
        .filter((d) => d && typeof d === 'object')
        .reduce((n, d) => n + Object.keys(d?.waves?.wave1?.assignments || {}).length, 0)
    : 0

  // Verify the sampled startingHoles landed at the same value on each team.
  const holeMismatches = []
  for (const s of sampleHoles) {
    const written = vFlights?.[s.fid]?.teams?.[s.tid]?.info?.startingHole
    if (written !== s.hole) {
      holeMismatches.push({ ...s, written })
    }
  }
  // eslint-disable-next-line no-console
  console.log(
    `[AI] handleCopyTournament verify: teeTimesMatch=${teeTimesMatch} · ` +
    `wave1 assignments src=${wave1AssignmentCount}/dst=${vWave1AssignmentCount} · ` +
    `startingHole mismatches=${holeMismatches.length}` +
    (holeMismatches.length ? ` ${JSON.stringify(holeMismatches)}` : '')
  )
  if (!teeTimesMatch || holeMismatches.length > 0) {
    return {
      success: false,
      message:
        `Copy failed verification — teeTimes/startingHole drift: ` +
        `teeTimesMatch=${teeTimesMatch}, mismatches=${holeMismatches.length}.`,
      tournamentId: newId,
    }
  }

  // Compose a detailed "what landed" summary for the director.
  const summary = [`${flightCount} flight${flightCount === 1 ? '' : 's'}`, `${writtenTeamCount} team${writtenTeamCount === 1 ? '' : 's'}`]
  if (teeTimesPresent) summary.push('tee times')
  if (srcContests) summary.push('contests')
  if (calcuttaCopy) summary.push('calcutta settings')
  if (srcRegistrations) summary.push(`${Object.keys(srcRegistrations).length} registrations`)
  if (srcQualifying) summary.push('qualifying state')
  if (srcPlayers) summary.push(`${Object.keys(srcPlayers).length} roster players`)

  return {
    success: true,
    message: `Copied "${srcInfo.name || 'source'}" → new tournament ${newId}: ${summary.join(', ')}.`,
    tournamentId: newId,
  }
}

// ─── SETUP_COMPLETE_TOURNAMENT ──────────────────────────────────────────────
//
// One-shot setup: reads qualified registrations from a source tournament,
// auto-derives team count from the qualifier list size, chains through
// handleCreateTournament (which seeds flights + tee times), then drops every
// qualified team into a flight slot in qualifier order (lowest qualifyingScore
// → top flight first — classic flighted-championship seeding).
//
// Source selection: if sourceTournamentId is explicit, use it. Otherwise walk
// every tournament and pick the most-recently-created one that has at least
// one registration with qualifyingStatus === 'complete'.
async function handleSetupCompleteTournament(p, _tournamentId, db) {
  const { name, date, format, startTime, startType, sourceTournamentId } = p
  if (!name) return { success: false, message: 'Setup tournament requires a name.' }

  // PBCC_TEMPLATE short-circuit. When the AI passes the sentinel (demo
  // mode or any "set up the 4-Ball" command), there is no qualifying
  // round to read — the template IS the source of truth. Go straight to
  // COPY without touching handleCreateTournament (which would otherwise
  // be called in Step 1 below). This mirrors the 4-Ball lockdown in
  // handleCreateTournament: no fallthrough, no shell write, no second
  // overwrite pass — the COPY's result is what the director gets.
  if (sourceTournamentId === PBCC_TEMPLATE_SENTINEL) {
    const resolved = await resolvePbccTemplate(db)
    if (!resolved || resolved.source !== 'template' || !(resolved.teamCount > 0)) {
      // eslint-disable-next-line no-console
      console.warn('[AI] handleSetupCompleteTournament → PBCC_TEMPLATE requested but no usable template in /templates')
      return {
        success: false,
        message:
          'No PBCC 4-Ball template found in /templates. ' +
          'Save a 4-Ball tournament as a template first ' +
          '(or click Try a Demo → Full Tournament Demo to seed one), then try again. ' +
          'I will not create an empty 4-Ball shell.',
      }
    }
    // eslint-disable-next-line no-console
    console.log(
      `[AI] handleSetupCompleteTournament → diverting to COPY from /${resolved.id} ` +
      `(template "${resolved.name}", ${resolved.teamCount} teams)`
    )
    const copyResult = await handleCopyTournament(
      { sourceTournamentId: resolved.id, newDate: date, newName: name },
      _tournamentId, db,
    )
    if (copyResult?.success) {
      return {
        ...copyResult,
        message: `Set up "${name}" from template /${resolved.id} — ${copyResult.message.replace(/^Copied[^:]+:\s*/, '')}`,
      }
    }
    return copyResult || {
      success: false,
      message: 'PBCC template COPY failed with no message.',
    }
  }

  // Resolve source tournament. PBCC_TEMPLATE sentinel → look up the
  // permanent /templates entry (preferred) or the live 4-Ball template
  // (fallback) via the shared resolver. The id may be path-prefixed
  // ('templates/pbcc-4-ball') when the source is the permanent store —
  // adjust the read path accordingly.
  let srcId = sourceTournamentId
  let source = null
  const sourcePathFor = (id) => id.startsWith('templates/') ? id : `tournaments/${id}`
  if (srcId === PBCC_TEMPLATE_SENTINEL) {
    const resolved = await resolvePbccTemplate(db)
    if (resolved) { srcId = resolved.id; source = (await get(tref(sourcePathFor(srcId)))).val() }
    else { srcId = null }
  }
  if (srcId && !source) {
    source = (await get(tref(sourcePathFor(srcId)))).val()
  }
  if (!srcId && !source) {
    const all = (await get(tref('tournaments'))).val() || {}
    const candidates = Object.entries(all)
      .map(([id, t]) => ({ id, t }))
      .filter(({ t }) => {
        const regs = Object.values(t?.registrations || {})
        return regs.some((r) => r?.qualifyingStatus === 'complete')
      })
      .sort((a, b) => (b.t?.info?.createdAt || 0) - (a.t?.info?.createdAt || 0))
    if (candidates.length) { srcId = candidates[0].id; source = candidates[0].t }
  }
  if (!source) {
    return { success: false, message: 'No source tournament with qualified registrations found.' }
  }

  // Collect qualified registrations, sort by qualifyingScore ASC (low = better).
  const qualified = Object.entries(source.registrations || {})
    .map(([rid, v]) => ({ rid, ...(v || {}) }))
    .filter((r) => r.qualifyingStatus === 'complete')
    .sort((a, b) => (a.qualifyingScore ?? 999) - (b.qualifyingScore ?? 999))

  if (qualified.length === 0) {
    return { success: false, message: 'Source tournament has 0 qualified teams.' }
  }

  // Step 1 — create the new tournament. handleCreateTournament auto-builds
  // flights sized to numTeams and seeds tee times based on startType.
  const numTeams = qualified.length
  const createResult = await handleCreateTournament({
    name, date, format,
    numTeams,
    numPlayers: numTeams * 4,
    startTime, startType,
    holesPerRound: 18,
  }, _tournamentId, db)
  if (!createResult?.success || !createResult.tournamentId) {
    return { success: false, message: `Setup failed at create step: ${createResult?.message}` }
  }
  const newId = createResult.tournamentId

  // Step 2 — re-read the freshly created tree so we know which flight ids and
  // team slot ids handleCreateTournament chose, then drop qualifiers into
  // those slots in qualifier order.
  const newTree = (await get(tref(`tournaments/${newId}`))).val() || {}
  const flightIds = Object.keys(newTree.flights || {}).filter((k) => !k.startsWith('_'))
  const updates = {}
  let placed = 0
  for (const fid of flightIds) {
    const teamIds = Object.keys(newTree.flights[fid].teams || {})
    for (const teamId of teamIds) {
      if (placed >= qualified.length) break
      const reg = qualified[placed]
      const existing = newTree.flights[fid].teams[teamId].info || {}
      updates[`tournaments/${newId}/flights/${fid}/teams/${teamId}/info`] = {
        name: reg.teamName || existing.name || `Team ${placed + 1}`,
        players: Array.isArray(reg.players) ? reg.players : (existing.players || []),
        startingHole: existing.startingHole || 1,
        qualifyingScore: reg.qualifyingScore ?? null,
        qualifyingRegId: reg.rid,
      }
      placed++
    }
    if (placed >= qualified.length) break
  }
  await updateTenant(updates)

  const sourceName = source.info?.name || srcId.slice(-6)
  return {
    success: true,
    message: `Set up "${name}" (id ${newId}): seeded ${placed} qualified team${placed === 1 ? '' : 's'} from "${sourceName}" into ${flightIds.length} flight${flightIds.length === 1 ? '' : 's'}. Tee times generated.`,
    tournamentId: newId,
  }
}

// ─── OPEN_CALCUTTA / CLOSE_CALCUTTA ─────────────────────────────────────────
//
// The live data model stores the gate at calcutta/settings.auctionOpen (see
// src/lib/data.js placeCalcuttaBid). We also stamp opened/closedAt so the
// AI log + Calcutta tab can show "last opened at …".
async function handleOpenCalcutta(_p, tournamentId, db) {
  await update(tref(`tournaments/${tournamentId}/calcutta/settings`), {
    auctionOpen: true,
  })
  const openedAtSnap = await get(tref(`tournaments/${tournamentId}/calcutta/openedAt`))
  if (openedAtSnap.val() == null) {
    await set(tref(`tournaments/${tournamentId}/calcutta/openedAt`), serverTimestamp())
  }
  return { success: true, message: 'Calcutta bidding is OPEN.' }
}

async function handleCloseCalcutta(_p, tournamentId, db) {
  await update(tref(`tournaments/${tournamentId}/calcutta/settings`), {
    auctionOpen: false,
  })
  await set(tref(`tournaments/${tournamentId}/calcutta/closedAt`), serverTimestamp())
  return { success: true, message: 'Calcutta bidding is CLOSED.' }
}

// Read-only actions (CALCUTTA_STANDINGS, CALCULATE_PAYOUTS) don't mutate
// anything — the AI assembles the human-readable answer inside `confirmation`.
async function handleReadOnlyNoop(_p, _tournamentId, _db) {
  return { success: true, message: 'Read-only — nothing to write.' }
}

// ─── IMPORT_LAST_TOURNAMENT_PLAYERS ─────────────────────────────────────────
async function handleImportLastTournamentPlayers(p, tournamentId, db) {
  const sourceId = p.sourceTournamentId
  const targetId = p.targetTournamentId || tournamentId
  if (!sourceId || !targetId) {
    return { success: false, message: 'Import players requires sourceTournamentId + targetTournamentId.' }
  }
  const [srcSnap, tgtSnap] = await Promise.all([
    get(tref(`tournaments/${sourceId}/players`)),
    get(tref(`tournaments/${targetId}/players`)),
  ])
  const src = srcSnap.val() || {}
  const tgt = tgtSnap.val() || {}
  const existing = new Set(
    Object.values(tgt).map((p) => String(p?.name || '').trim().toLowerCase()).filter(Boolean),
  )
  let imported = 0
  for (const [, srcPlayer] of Object.entries(src)) {
    const nm = String(srcPlayer?.name || '').trim()
    if (!nm) continue
    if (existing.has(nm.toLowerCase())) continue
    const newRef = push(tref(`tournaments/${targetId}/players`))
    await set(newRef, srcPlayer)
    existing.add(nm.toLowerCase())
    imported++
  }
  return { success: true, message: `Imported ${imported} player${imported === 1 ? '' : 's'} (skipped duplicates).` }
}

// ─── AUTO_ASSIGN_TEAMS ──────────────────────────────────────────────────────
async function handleAutoAssignTeams(p, tournamentId, db) {
  const method = p.method || 'random'
  const teamSize = Math.max(1, Number(p.teamSize) || 2)
  const playersSnap = await get(tref(`tournaments/${tournamentId}/players`))
  const flightsSnap = await get(tref(`tournaments/${tournamentId}/flights`))
  const playersMap = playersSnap.val() || {}
  let roster = Object.entries(playersMap).map(([pid, v]) => ({
    pid,
    name: String(v?.name || ''),
    handicap: Number(v?.handicap ?? v?.handicapIndex ?? 99),
  })).filter((r) => r.name)
  if (roster.length === 0) return { success: false, message: 'No players in the roster yet.' }

  if (method === 'handicap') {
    roster.sort((a, b) => a.handicap - b.handicap)
  } else {
    for (let i = roster.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[roster[i], roster[j]] = [roster[j], roster[i]]
    }
  }

  const flights = flightsSnap.val() || {}
  const flightIds = Object.keys(flights)
  let targetFlight = flightIds[0]
  if (!targetFlight) {
    targetFlight = 'flightA'
    await set(tref(`tournaments/${tournamentId}/flights/${targetFlight}/info`), { name: 'Main Flight' })
  }

  const updates = {}
  let teamIdx = 0
  for (let i = 0; i < roster.length; i += teamSize) {
    const slice = roster.slice(i, i + teamSize)
    const teamId = `team-${String(teamIdx + 1).padStart(2, '0')}`
    updates[`tournaments/${tournamentId}/flights/${targetFlight}/teams/${teamId}/info`] = {
      name: slice.map((r) => r.name.split(/\s+/).pop()).join('/'),
      players: slice.map((r) => r.name),
      startingHole: 1,
    }
    teamIdx++
  }
  await updateTenant(updates)
  return { success: true, message: `Created ${teamIdx} team${teamIdx === 1 ? '' : 's'} of ${teamSize} (${method}).` }
}

// ─── FINALIZE_ROUND ─────────────────────────────────────────────────────────
//
// Delegates to closeTournamentWithResults in src/lib/data.js — the same
// helper the admin UI's "Close Tournament" button calls. It walks every
// flight, ranks teams by gross (low first), writes 1st/2nd/3rd to
// tournaments/{id}/results/flights/{fid}, then flips info.status to
// 'closed' and stamps closedAt. Saying "finalize results" now does the
// full close — leaderboard frozen, podium recorded — not just the
// status flag.
async function handleFinalizeRound(_p, tournamentId, db) {
  const snap = await get(tref(`tournaments/${tournamentId}`))
  const tournament = snap.val()
  if (!tournament) {
    return { success: false, message: 'Tournament not found.' }
  }
  await closeTournamentWithResults(tournamentId, tournament)
  const flightCount = Object.keys(tournament.flights || {}).filter(k => !k.startsWith('_')).length
  return {
    success: true,
    message: `Tournament finalized — top-3 recorded for ${flightCount} flight${flightCount === 1 ? '' : 's'}, status set to closed.`,
  }
}

// ─── NOTIFY_DELAY / POST_ANNOUNCEMENT ───────────────────────────────────────
//
// The codebase had no announcements node before. Shape introduced here:
//   tournaments/{id}/announcements/{pushId}: { kind, text, postedAt, delayMinutes? }
async function handleNotifyDelay(p, tournamentId, db) {
  const { message, delayMinutes } = p
  if (!message) return { success: false, message: 'NOTIFY_DELAY requires a message.' }
  const newRef = push(tref(`tournaments/${tournamentId}/announcements`))
  await set(newRef, {
    kind: 'delay',
    text: String(message),
    delayMinutes: Number.isFinite(Number(delayMinutes)) ? Number(delayMinutes) : null,
    postedAt: serverTimestamp(),
  })
  return { success: true, message: `Delay notice posted (${delayMinutes ?? '?'} min).` }
}

async function handlePostAnnouncement(p, tournamentId, db) {
  const { message, screen } = p
  if (!message) return { success: false, message: 'POST_ANNOUNCEMENT requires a message.' }
  // Show it on the TV(s) via the same tvControl channel the /tvcontrol panel
  // uses. screen omitted / "all" → global (every screen); a specific id → just
  // that TV (per-screen wins over global on the display side).
  const target = (screen == null || String(screen).trim() === '' || String(screen).toLowerCase() === 'all')
    ? 'all'
    : String(screen).trim()
  const ann = { text: String(message), postedAt: serverTimestamp() }
  if (target === 'all') {
    await update(tref('tvControl'), { announcement: ann })
  } else {
    await update(tref(`tvControl/screens/${target}`), { announcement: ann })
  }
  // Also log it to the tournament announcement history (unchanged behavior).
  const newRef = push(tref(`tournaments/${tournamentId}/announcements`))
  await set(newRef, {
    kind: 'general',
    text: String(message),
    target,
    postedAt: serverTimestamp(),
  })
  return { success: true, message: target === 'all' ? 'Announcement posted to all TV screens.' : `Announcement posted to TV ${target}.` }
}

// ─── RESET_SCORES ───────────────────────────────────────────────────────────
//
// Scope:
//   'all'    — remove every score map from every team.
//   'flight' — remove every score map inside flightId.
//   'hole'   — remove just holeNumber from every team (or just within flightId
//              if flightId is supplied).
async function handleResetScores(p, tournamentId, db) {
  const scope = p.scope || 'hole'
  const flightId = p.flightId || null
  const holeNumber = p.holeNumber != null ? Number(p.holeNumber) : null

  const flightsSnap = await get(tref(`tournaments/${tournamentId}/flights`))
  const flights = flightsSnap.val() || {}
  const updates = {}
  let count = 0

  for (const [fid, f] of Object.entries(flights)) {
    if (scope === 'flight' && fid !== flightId) continue
    if (scope === 'hole' && flightId && fid !== flightId) continue
    for (const [tid, team] of Object.entries(f?.teams || {})) {
      if (scope === 'hole') {
        if (!holeNumber) continue
        if (team?.scores && team.scores[holeNumber] != null) {
          updates[`tournaments/${tournamentId}/flights/${fid}/teams/${tid}/scores/${holeNumber}`] = null
          count++
        }
      } else {
        // all / flight — nuke the entire scores map
        updates[`tournaments/${tournamentId}/flights/${fid}/teams/${tid}/scores`] = null
        if (team?.scores) count += Object.keys(team.scores).length
      }
    }
  }
  if (Object.keys(updates).length === 0) {
    return { success: false, message: 'No scores matched the requested scope.' }
  }
  await updateTenant(updates)
  const label = scope === 'all' ? 'all flights'
    : scope === 'flight' ? `flight ${flightId}`
    : `hole ${holeNumber}${flightId ? ` in flight ${flightId}` : ''}`
  return { success: true, message: `Reset ${count} score${count === 1 ? '' : 's'} (${label}).` }
}

// ─── PBCC_BUILD_FROM_QUALIFYING ─────────────────────────────────────────────
//
// Clone the appropriate ratliff template (12-flight ≤96 teams, 13-flight
// >96) into the active tournament, then rebuild flights from
// the real qualifying list and translate the template's tee-time + bracket
// team references to the new flight teams (by qualifying-score position).
//
// Pipeline:
//   1. Read template (info, calcutta, teeTimes, brackets)
//   2. Read current tournament info + registrations (preserved)
//   3. Multi-path clone: template's calcutta + teeTimes + brackets land in
//      the new tournament; info is merged (template defaults + new
//      tournament's name/date/course/createdAt); /flights cleared so
//      autoFlightRegistrations doesn't append-with-suffix.
//   4. autoFlightRegistrations(qualifyingScore) — writes flight00..flightNN
//      with team_1..team_M in global qualifying order.
//   5. autoPairAllFlights — sets info.opponentId on every team (1↔N).
//   6. Build position-based teamIdMap (template flight teams sorted by
//      qualifyingScore → new flight teams sorted same).
//   7. Translate template teeTimes assignments via teamIdMap; derive
//      info.startingHole on each new team from those assignments.
//   8. Translate template brackets {a,b,winnerId} via teamIdMap.
async function handlePbccBuildFromQualifying(_p, tournamentId, db) {
  if (!tournamentId) {
    return { success: false, message: 'PBCC build requires an active tournament — open the tournament admin first.' }
  }

  // Read registrations first — the qualified-team count picks which
  // template (12-flight vs 13-flight) we clone from.
  const [infoSnap, regsSnap] = await Promise.all([
    get(tref(`tournaments/${tournamentId}/info`)),
    get(tref(`tournaments/${tournamentId}/registrations`)),
  ])
  const currentInfo = infoSnap.val() || {}
  const registrations = regsSnap.val() || {}
  const qualified = Object.entries(registrations).filter(([, r]) =>
    Number.isFinite(Number(r?.qualifyingScore))
  )
  if (qualified.length === 0) {
    return {
      success: false,
      message: 'No registrations have a qualifyingScore yet. Click "Accept All Qualifying Scores" on the Qualifying tab first.',
    }
  }
  const numTeams = qualified.length
  // 8 teams per flight → the field decides the flight count, 1–13. 96 → 12,
  // 104 → 13, and anything between builds the exact number of 8-team flights it
  // needs (a single 8-team event builds just the Championship flight). The ≤96
  // template (12 flights) covers 1–12; the 13-flight template covers 13.
  const numFlights = Math.max(1, Math.min(13, Math.ceil(numTeams / 8)))
  const templatePath = numFlights <= 12 ? TEMPLATE_PATH_12 : TEMPLATE_PATH_13

  const templateSnap = await get(tref(templatePath))
  const template = templateSnap.val()
  if (!template) {
    // No PBCC template in this database (e.g. a fresh site / the test copy).
    // Fall back to a faithful PLAIN build straight from the qualified
    // registrations so "Build" still works: flights by qualifying score,
    // 1↔N pairings, and a bracket per flight. Calcutta + preset tee-times
    // only exist inside a template, so those are simply left unset.
    const plainInfo = {
      ...currentInfo,
      status: currentInfo.status || 'open',
      flightsLocked: false,
    }
    await updateTenant({
      [`tournaments/${tournamentId}/info`]: plainInfo,
      [`tournaments/${tournamentId}/flights`]: null,
    })
    await autoFlightRegistrations(tournamentId, registrations, { numFlights, sortBy: 'qualifyingScore' })
    await autoPairAllFlights(tournamentId)
    const plainBracketDays = Math.max(1, Number(currentInfo.bracketDays) || Number(currentInfo.tournamentDays) || 1)
    const fsnap = await get(tref(`tournaments/${tournamentId}/flights`))
    const flts = fsnap.val() || {}
    for (const fid of Object.keys(flts)) {
      if (fid.startsWith('_')) continue
      await generateBracket(tournamentId, fid, plainBracketDays)
    }
    // Team-vs-team shotgun: each match's two teams share a hole (1A, 1B, …).
    await assignShotgunHolesByMatch(tournamentId)
    return {
      success: true,
      message: `Built ${numTeams} qualified team${numTeams === 1 ? '' : 's'} into ${numFlights} flight${numFlights === 1 ? '' : 's'} (plain build — no PBCC template in this database).`,
      tournamentId,
    }
  }
  // Step 3 — clone template (skip its flights + registrations + meta).
  const mergedInfo = {
    ...(template.info || {}),
    name:      currentInfo.name      || template.info?.name,
    date:      currentInfo.date      || template.info?.date,
    course:    currentInfo.course    || template.info?.course,
    createdAt: currentInfo.createdAt || template.info?.createdAt || Date.now(),
    status:    currentInfo.status    || 'open',
    flightsLocked: false,
  }
  // The template can carry demo/test/practice flags — scrub them so a REAL
  // build isn't mistaken for a demo. Mirrors handleCopyTournament's scrub.
  delete mergedInfo.isDemo
  delete mergedInfo.isTestStation
  delete mergedInfo.testStationTile
  delete mergedInfo.testStationCreatedAt
  delete mergedInfo.demoCreatedAt
  delete mergedInfo.practice
  // ...but if the shell being built was INTENTIONALLY a demo/practice (e.g. a
  // demo-roster event), keep that label so it stays a show-off, not a real one.
  if (currentInfo.isDemo) { mergedInfo.isDemo = true; mergedInfo.demoCreatedAt = currentInfo.demoCreatedAt || Date.now() }
  if (currentInfo.practice) mergedInfo.practice = true
  const cloneWrites = {
    [`tournaments/${tournamentId}/info`]:     mergedInfo,
    [`tournaments/${tournamentId}/flights`]:  null,
  }
  if (template.calcutta) cloneWrites[`tournaments/${tournamentId}/calcutta`] = template.calcutta
  if (template.teeTimes) cloneWrites[`tournaments/${tournamentId}/teeTimes`] = template.teeTimes
  if (template.brackets) cloneWrites[`tournaments/${tournamentId}/brackets`] = template.brackets
  await updateTenant(cloneWrites)

  // Demo/practice build at the template's EXACT size → clone the template's
  // flights VERBATIM (scores stripped), skipping the rebuild below. This makes
  // the result byte-identical to the Full Tournament Demo (which COPYs the same
  // template) — same flights, same 1v8 pairings, same waves, same brackets — so
  // the demo flow and Submit-to-Tournament produce the SAME PBCC structure.
  // Real fields (and off-sizes) fall through to the faithful rebuild below.
  const templateTeamCount = Object.values(template.flights || {})
    .reduce((n, f) => n + Object.keys(f?.teams || {}).length, 0)
  if ((currentInfo.isDemo || currentInfo.isPractice || currentInfo.practice) && templateTeamCount === numTeams) {
    const flightsVerbatim = JSON.parse(JSON.stringify(template.flights))
    for (const f of Object.values(flightsVerbatim)) {
      for (const t of Object.values(f?.teams || {})) { if (t && t.scores) delete t.scores }
    }
    await updateTenant({ [`tournaments/${tournamentId}/flights`]: flightsVerbatim })
    return {
      success: true,
      message: `Cloned /${templatePath} verbatim (demo/practice): ${numTeams} teams — identical PBCC structure to the Full Tournament Demo.`,
      tournamentId,
    }
  }

  // Step 4 — rebuild flights from real qualifying registrations.
  await autoFlightRegistrations(tournamentId, registrations, {
    numFlights,
    sortBy: 'qualifyingScore',
  })

  // Step 5 — pair every flight 1↔N by qualifying-score order.
  await autoPairAllFlights(tournamentId)

  // Step 6 — position-based teamIdMap (qualifying-score order within flight).
  const newFlightsSnap = await get(tref(`tournaments/${tournamentId}/flights`))
  const newFlights = newFlightsSnap.val() || {}
  const sortByQScore = (teams) =>
    Object.entries(teams || {})
      .map(([id, t]) => ({ id, q: Number(t?.info?.qualifyingScore) }))
      .sort((a, b) => {
        const aQ = Number.isFinite(a.q) ? a.q : Infinity
        const bQ = Number.isFinite(b.q) ? b.q : Infinity
        if (aQ !== bQ) return aQ - bQ
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
      })
      .map((t) => t.id)
  const teamIdMap = {}
  for (const [flightId, tplFlight] of Object.entries(template.flights || {})) {
    const newFlight = newFlights[flightId]
    if (!newFlight) continue
    const tplOrder = sortByQScore(tplFlight.teams)
    const newOrder = sortByQScore(newFlight.teams)
    const len = Math.min(tplOrder.length, newOrder.length)
    for (let i = 0; i < len; i++) teamIdMap[tplOrder[i]] = newOrder[i]
  }
  const teamToFlight = {}
  for (const [fid, f] of Object.entries(newFlights)) {
    for (const tid of Object.keys(f.teams || {})) teamToFlight[tid] = fid
  }

  // Step 7 — tee times. An EXACT-size, non-member field IS the template's own
  // field, so clone the template's tee sheet VERBATIM (same waves, start times,
  // flight-to-wave, holes) and ONLY remap the assignment team ids by
  // qualifying-score position — the output is the template's tee sheet EXACTLY,
  // no recompute (this mirrors how the brackets are translated verbatim below).
  // Off-size / member fields can't fit the template layout as-is, so they fall
  // back to a computed 8:00 AM / 1:00 PM wave split.
  const hasMembers = Object.values(registrations).some((r) => r && r.member === true)
  const useTemplateTeeSheet = numTeams === templateTeamCount && !hasMembers
  // Object.entries handles BOTH an object map and an array. Crucially, for a
  // SPARSE array — RTDB omits absent day indexes, so a tee sheet with only day
  // "1" comes back as [<hole>, {…}] — Object.entries SKIPS the holes, returning
  // just the real [dayKey, dayObj] pairs. (.map() would keep the hole and feed
  // `undefined` into the for-of destructuring below → ".for is not iterable".)
  const tplDays = Object.entries(template.teeTimes || {})

  const writes = {}
  const startingHoleByTeam = {}
  if (useTemplateTeeSheet) {
    for (const [day, dobj] of tplDays) {
      if (!dobj || !dobj.waves) continue
      for (const [wk, w] of Object.entries(dobj.waves)) {
        const assignments = {}
        for (const [oldTid, hole] of Object.entries(w.assignments || {})) {
          const newTid = teamIdMap[oldTid]
          if (newTid) {
            assignments[newTid] = hole
            if (day === '1') startingHoleByTeam[newTid] = hole
          }
        }
        writes[`tournaments/${tournamentId}/teeTimes/${day}/waves/${wk}/startTime`] = w.startTime
        writes[`tournaments/${tournamentId}/teeTimes/${day}/waves/${wk}/flights`] = w.flights || {}
        writes[`tournaments/${tournamentId}/teeTimes/${day}/waves/${wk}/assignments`] = assignments
      }
    }
  } else {
    const sortedFlightIds = Object.keys(newFlights).filter((k) => !k.startsWith('_')).sort()
    const half = Math.ceil(sortedFlightIds.length / 2)
    const waveLayout = sortedFlightIds.length <= 8
      ? [{ key: 'wave1', time: '8:00 AM', flights: sortedFlightIds }]
      : [
          { key: 'wave1', time: '8:00 AM', flights: sortedFlightIds.slice(0, half) },
          { key: 'wave2', time: '1:00 PM', flights: sortedFlightIds.slice(half) },
        ]
    const layoutKeys = new Set(waveLayout.map((w) => w.key))
    const holeByNewTeam = {}
    for (const [, dayObj] of tplDays) {
      for (const wave of Object.values(dayObj?.waves || {})) {
        for (const [oldTid, hole] of Object.entries(wave?.assignments || {})) {
          const newTid = teamIdMap[oldTid]
          if (newTid) holeByNewTeam[newTid] = hole
        }
      }
    }
    const numDays = Math.max(1, Number(mergedInfo.bracketDays) || Number(mergedInfo.tournamentDays) || tplDays.length || 1)
    for (let d = 1; d <= numDays; d++) {
      const day = String(d)
      for (const w of waveLayout) {
        const flightsMap = {}
        const assignments = {}
        for (const fid of w.flights) {
          flightsMap[fid] = true
          for (const tid of Object.keys(newFlights[fid]?.teams || {})) {
            const hole = holeByNewTeam[tid]
            if (hole != null) {
              assignments[tid] = hole
              if (day === '1') startingHoleByTeam[tid] = hole
            }
          }
        }
        writes[`tournaments/${tournamentId}/teeTimes/${day}/waves/${w.key}/startTime`] = w.time
        writes[`tournaments/${tournamentId}/teeTimes/${day}/waves/${w.key}/flights`] = flightsMap
        writes[`tournaments/${tournamentId}/teeTimes/${day}/waves/${w.key}/assignments`] = assignments
      }
      const tplDayObj = tplDays.find(([dd]) => dd === day)?.[1]
      for (const waveKey of Object.keys(tplDayObj?.waves || {})) {
        if (!layoutKeys.has(waveKey)) writes[`tournaments/${tournamentId}/teeTimes/${day}/waves/${waveKey}`] = null
      }
    }
  }
  for (const [tid, hole] of Object.entries(startingHoleByTeam)) {
    const fid = teamToFlight[tid]
    if (fid) writes[`tournaments/${tournamentId}/flights/${fid}/teams/${tid}/info/startingHole`] = hole
  }

  // Step 8 — translate bracket team IDs (a / b / winnerId).
  for (const [flightId, fbr] of Object.entries(template.brackets || {})) {
    for (const [rKey, round] of Object.entries(fbr?.rounds || {})) {
      for (const [mKey, match] of Object.entries(round?.matches || {})) {
        for (const field of ['a', 'b', 'winnerId']) {
          const old = match?.[field]
          const next = old && teamIdMap[old]
          if (next) {
            writes[`tournaments/${tournamentId}/brackets/${flightId}/rounds/${rKey}/matches/${mKey}/${field}`] = next
          }
        }
      }
    }
  }
  if (Object.keys(writes).length > 0) await updateTenant(writes)

  // Step 9 — make sure EVERY flight is bracketed. The template only carries
  // brackets for the flights it had; generate one for any flight the clone
  // didn't cover (or all of them if the template had none). Same seeding as
  // the "Generate ALL brackets" button — R1 by qualifying order (1v8…4v5).
  const bracketDays =
    Number(Object.values(template.brackets || {})[0]?.bracketDays) ||
    Number(template.info?.bracketDays) ||
    3
  const coveredFlightIds = new Set(Object.keys(template.brackets || {}))
  const missingFlightIds = Object.keys(newFlights).filter(
    (fid) => !fid.startsWith('_') && !coveredFlightIds.has(fid),
  )
  for (const fid of missingFlightIds) {
    await generateBracket(tournamentId, fid, bracketDays)
  }

  // Partial field: the template's tee sheet + bracket were laid out for its OWN
  // full pairing (1↔8 across 8-team flights). When the real field doesn't fill
  // the template, translating those by position scatters our re-derived 1↔N
  // pairing onto DIFFERENT holes (paired teams split apart — reads as one-on-one).
  // Rebuild the tee sheet + brackets cleanly from the ACTUAL pairings so every
  // match is team-vs-team on one shared hole (1A, 1B, …). Exact-size fields keep
  // the template layout verbatim.
  // Also rebuild when the field has MEMBER teams — the template bracket is a
  // fixed 1↔8 positional layout and would ignore the member-aware round-1
  // pairing (no Member vs Member). Rebuilding from the actual opponentId pairs
  // makes the bracket + tee sheet honor it. (hasMembers computed in Step 7.)
  if (numTeams !== templateTeamCount || hasMembers) {
    await updateTenant({ [`tournaments/${tournamentId}/brackets`]: null })
    for (const fid of Object.keys(newFlights)) {
      if (fid.startsWith('_')) continue
      await generateBracket(tournamentId, fid, bracketDays)
    }
    await assignShotgunHolesByMatch(tournamentId)
  }

  return {
    success: true,
    message:
      `Built /${templatePath}: ${numTeams} qualified team${numTeams === 1 ? '' : 's'} into ${numFlights} flight${numFlights === 1 ? '' : 's'}` +
      (numTeams !== templateTeamCount || hasMembers
        ? ` (tee sheet + brackets rebuilt from the actual pairings${hasMembers ? ', member-aware round 1' : ''}).`
        : `. Calcutta + tee times + brackets carried over from template (${Object.keys(teamIdMap).length} ID mappings).`),
    tournamentId,
  }
}

// ─── helpers ────────────────────────────────────────────────────────────────

async function readInfo(db, tournamentId, flightId, teamId) {
  const snap = await get(tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/info`))
  return snap.val()
}

// players[] entries are usually strings, but MARK_NO_SHOW promotes them to
// objects { name, noShow }. Match against whichever the entry is.
function findPlayerIndex(list, name) {
  const norm = String(name || '').trim().toLowerCase()
  if (!norm) return -1
  for (let i = 0; i < list.length; i++) {
    const entry = list[i]
    const n = typeof entry === 'string' ? entry : (entry?.name || '')
    if (String(n).trim().toLowerCase() === norm) return i
  }
  // Partial fallback — first matching first/last name.
  for (let i = 0; i < list.length; i++) {
    const entry = list[i]
    const n = String(typeof entry === 'string' ? entry : (entry?.name || '')).trim().toLowerCase()
    const parts = n.split(/\s+/)
    if (parts.includes(norm)) return i
    if (norm.split(/\s+/).every((p) => n.includes(p))) return i
  }
  return -1
}

function findPlayerIdByName(players, name) {
  const norm = String(name || '').trim().toLowerCase()
  if (!norm) return null
  // Exact first.
  for (const [pid, p] of Object.entries(players)) {
    if (String(p?.name || '').trim().toLowerCase() === norm) return pid
  }
  // Partial second.
  for (const [pid, p] of Object.entries(players)) {
    const n = String(p?.name || '').trim().toLowerCase()
    const parts = n.split(/\s+/)
    if (parts.includes(norm)) return pid
    if (norm.split(/\s+/).every((piece) => n.includes(piece))) return pid
  }
  return null
}

// Accept "8:00 AM", "08:00", "7am", "7:30 pm", etc. → canonical "H:MM AM/PM"
// string. Returns null if unparseable.
function normalizeTimeString(s) {
  if (!s) return null
  const raw = String(s).trim()
  // "8:00 AM" or "12:30 pm"
  let m = raw.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i)
  if (m) {
    return `${parseInt(m[1], 10)}:${m[2]} ${m[3].toUpperCase()}`
  }
  // "7am" or "12 PM"
  m = raw.match(/^(\d{1,2})\s*(am|pm)$/i)
  if (m) return `${parseInt(m[1], 10)}:00 ${m[2].toUpperCase()}`
  // "08:00" or "8:00" 24-hour
  m = raw.match(/^(\d{1,2}):(\d{2})$/)
  if (m) {
    const h = parseInt(m[1], 10)
    const mm = m[2]
    const ap = h >= 12 ? 'PM' : 'AM'
    const h12 = ((h + 11) % 12) + 1
    return `${h12}:${mm} ${ap}`
  }
  return null
}

// "8:00 AM" + 20  →  "8:20 AM".  "11:50 AM" + 20  →  "12:10 PM".
// Returns null if the input doesn't look like a clock string.
function shiftTimeString(s, mins) {
  const m = String(s || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!m) return null
  let h = parseInt(m[1], 10)
  const mm = parseInt(m[2], 10)
  const ap = m[3].toUpperCase()
  if (h === 12) h = 0
  if (ap === 'PM') h += 12
  let total = h * 60 + mm + Number(mins)
  // Wrap into 24h.
  total = ((total % (24 * 60)) + 24 * 60) % (24 * 60)
  const nh = Math.floor(total / 60)
  const nm = total % 60
  const nAp = nh >= 12 ? 'PM' : 'AM'
  const h12 = ((nh + 11) % 12) + 1
  return `${h12}:${String(nm).padStart(2, '0')} ${nAp}`
}

// ─── Read-only club lookups ─────────────────────────────────────────────────
//
// Answer handicap & course questions straight from the SAME club catalog the
// Handicap & Courses (ClubData) page uses: club/players/{id} and
// club/courses/{id}. Pure reads — no writes. When a player or course can't be
// matched we return success:true with a plain "not found" sentence so the AI
// panel shows it as a normal answer, not a red error.
//
//   club/players/{id} = { name, email, ghin, handicapIndex }
//   club/courses/{id} = { courseName, coursePar, strokeIndexMen[18],
//                         strokeIndexWomen[18], strokeIndex[18] (legacy),
//                         teeBoxes:[{ name, rating, slope, par }] }

function ci(s) { return String(s || '').trim().toLowerCase() }

function fuzzyMatchName(needle, hay) {
  const q = ci(needle)
  const n = ci(hay)
  if (!q || !n) return false
  if (n === q || n.includes(q) || q.includes(n)) return true
  const nt = n.split(/\s+/)
  return nt.some((t) => t === q) || q.split(/\s+/).some((t) => t.length > 2 && nt.includes(t))
}

function teeBoxesOf(course) {
  if (Array.isArray(course?.teeBoxes) && course.teeBoxes.length > 0) return course.teeBoxes
  // Legacy single-tee record.
  if (course?.teeName && Number(course?.slopeRating) > 0 && Number(course?.courseRating) > 0) {
    return [{ name: course.teeName, rating: Number(course.courseRating), slope: Number(course.slopeRating), par: Number(course.coursePar) || 72 }]
  }
  return []
}

function strokeIndexOf(course) {
  if (Array.isArray(course?.strokeIndexMen) && course.strokeIndexMen.length) return course.strokeIndexMen
  if (Array.isArray(course?.strokeIndex) && course.strokeIndex.length) return course.strokeIndex
  if (Array.isArray(course?.strokeIndexWomen) && course.strokeIndexWomen.length) return course.strokeIndexWomen
  return null
}

async function findClubCourse(db, courseName) {
  const snap = await get(tref('club/courses'))
  const all = snap.val() || {}
  const list = Object.entries(all).map(([id, v]) => ({ id, ...(v || {}) }))
  if (!courseName) return { list, matches: [] }
  const matches = list.filter((c) => fuzzyMatchName(courseName, c.courseName))
  return { list, matches }
}

async function handlePlayerHandicapLookup(p, _tournamentId, db) {
  const name = p?.playerName
  if (!name) return { success: true, message: 'Which player? Give me a name and I’ll look up their handicap.' }
  const snap = await get(tref('club/players'))
  const players = Object.entries(snap.val() || {}).map(([id, v]) => ({ id, ...(v || {}) }))
  const matches = players.filter((pl) => fuzzyMatchName(name, pl.name))
  if (matches.length === 0) {
    return { success: true, message: `I don’t have a player named “${name}” in the club roster.` }
  }
  if (matches.length > 1) {
    const names = matches.map((m) => m.name).join(', ')
    return { success: true, message: `More than one roster player matches “${name}”: ${names}. Which one?` }
  }
  const pl = matches[0]
  const idx = pl.handicapIndex
  const ghinTxt = pl.ghin ? ` (GHIN ${pl.ghin})` : ''
  if (idx == null || idx === '') {
    return { success: true, message: `${pl.name} is in the roster${ghinTxt}, but there’s no handicap index on file yet.` }
  }
  let msg = `${pl.name}’s handicap index is ${idx}${ghinTxt}.`

  // Optional course handicap when a course is named.
  if (p?.courseName) {
    const { matches: cMatches } = await findClubCourse(db, p.courseName)
    if (cMatches.length === 0) {
      msg += ` I couldn’t find a saved course named “${p.courseName}” to compute a course handicap.`
    } else {
      const course = cMatches[0]
      const tees = teeBoxesOf(course)
      const par = Number(course.coursePar) || 72
      const wanted = p?.teeName ? tees.filter((t) => fuzzyMatchName(p.teeName, t.name)) : tees
      if (tees.length === 0) {
        msg += ` ${course.courseName} has no tee rating/slope saved, so I can’t compute a course handicap.`
      } else if (p?.teeName && wanted.length === 0) {
        const names = tees.map((t) => t.name).join(', ')
        msg += ` No “${p.teeName}” tee at ${course.courseName}. Saved tees: ${names}.`
      } else {
        const parts = wanted.map((t) => {
          const ch = calcCourseHandicap(idx, t.slope, t.rating, t.par || par)
          return `${t.name} (${t.rating}/${t.slope}) → ${ch}`
        })
        msg += ` Course handicap at ${course.courseName}: ${parts.join('; ')}.`
      }
    }
  }
  return { success: true, message: msg }
}

async function handleCourseLookup(p, _tournamentId, db) {
  const courseName = p?.courseName
  if (!courseName) return { success: true, message: 'Which course? Name a saved course and I’ll pull its data.' }
  const { list, matches } = await findClubCourse(db, courseName)
  if (matches.length === 0) {
    const saved = list.map((c) => c.courseName).filter(Boolean)
    const savedTxt = saved.length ? ` Saved courses: ${saved.join(', ')}.` : ' No courses are saved yet.'
    return { success: true, message: `I don’t have a saved course named “${courseName}”.${savedTxt}` }
  }
  const course = matches[0]
  const par = Number(course.coursePar) || null
  const tees = teeBoxesOf(course)
  const si = strokeIndexOf(course)
  const hole = Number.isFinite(Number(p?.hole)) ? Number(p.hole) : null
  // Default: hole given with no explicit field → stroke index; else "all".
  const field = p?.field || (hole ? 'strokeIndex' : 'all')

  // Per-hole stroke index.
  if (field === 'strokeIndex' && hole) {
    if (!si) return { success: true, message: `${course.courseName} has no stroke index saved.` }
    if (hole < 1 || hole > si.length) return { success: true, message: `Hole ${hole} is out of range — ${course.courseName} has ${si.length} stroke-index holes on file.` }
    return { success: true, message: `Stroke index on hole ${hole} at ${course.courseName} is ${si[hole - 1]}.` }
  }
  if (field === 'strokeIndex') {
    if (!si) return { success: true, message: `${course.courseName} has no stroke index saved.` }
    const listed = si.map((v, i) => `${i + 1}:${v}`).join('  ')
    return { success: true, message: `Stroke index for ${course.courseName} (hole:SI)\n${listed}` }
  }

  // Rating / slope per tee.
  if (field === 'rating' || field === 'slope') {
    if (tees.length === 0) return { success: true, message: `${course.courseName} has no tee rating/slope saved.` }
    const wanted = p?.teeName ? tees.filter((t) => fuzzyMatchName(p.teeName, t.name)) : tees
    if (p?.teeName && wanted.length === 0) {
      return { success: true, message: `No “${p.teeName}” tee at ${course.courseName}. Saved tees: ${tees.map((t) => t.name).join(', ')}.` }
    }
    const parts = wanted.map((t) => `${t.name}: rating ${t.rating}, slope ${t.slope}`)
    return { success: true, message: `${course.courseName} — ${parts.join('; ')}.` }
  }

  // Pars — we store TOTAL par + per-tee par, but NOT a per-hole par array.
  if (field === 'pars') {
    if (hole) {
      return { success: true, message: `I don’t have per-hole par saved for ${course.courseName} — only the total (${par ?? 'n/a'}) and per-tee par. So I can’t give par for a single hole.` }
    }
    const teePar = tees.length ? ` Per tee: ${tees.map((t) => `${t.name} par ${t.par || par}`).join('; ')}.` : ''
    return { success: true, message: `${course.courseName} total par is ${par ?? 'not set'}.${teePar} (Per-hole par isn’t saved.)` }
  }

  // field === 'all' → a compact summary.
  const teeTxt = tees.length ? tees.map((t) => `${t.name} ${t.rating}/${t.slope} (par ${t.par || par})`).join('; ') : 'no tees saved'
  const siTxt = si ? `stroke index on file for ${si.length} holes` : 'no stroke index saved'
  return { success: true, message: `${course.courseName}: total par ${par ?? 'not set'}. Tees — ${teeTxt}. ${siTxt}. (Per-hole par isn’t saved; ask me for stroke index on a hole, or a tee’s rating/slope.)` }
}

// GHIN lookup — reuses the EXISTING lookupGhinHandicap (the same call the
// handicap-screen auto-fill uses). Read-only; we do not touch GHIN auth or
// its cache. Returns the golfer's name + index, or a plain failure sentence.
async function handleGhinLookup(p, _tournamentId, _db) {
  const raw = String(p?.ghinNumber ?? '').replace(/\D/g, '')
  if (!raw) return { success: true, message: 'Give me a GHIN number and I’ll look up the handicap.' }
  const res = await lookupGhinHandicap(raw)
  if (res?.ok) {
    const who = res.fullName || 'That golfer'
    return { success: true, message: `${who} — handicap index ${res.handicapIndex} (GHIN ${raw}).` }
  }
  if (res?.reason === 'notfound') {
    return { success: true, message: `No golfer found for GHIN ${raw}. Double-check the number.` }
  }
  return { success: true, message: `The GHIN lookup couldn’t reach the service just now (${res?.reason || 'error'}). Try again in a moment.` }
}

// ─── GHIN / handicap system (AI GHIN completion) ────────────────────────────
// Course params for buildHandicapEntry, read from tournament info. calc helpers
// are NaN-safe, and snapshotNetHandicaps recomputes from the index anyway.
function courseParamsFor(info) {
  return {
    slopeRating: Number(info?.slopeRating),
    courseRating: Number(info?.courseRating),
    coursePar: Number(info?.coursePar),
    allowance: Number.isFinite(Number(info?.handicapAllowance)) ? Number(info.handicapAllowance) : 100,
  }
}

// PULL_GHINS_FOR_FIELD — batch-pull every registered player's index from their
// GHIN (registration ghin, else club-roster fallback) and stamp source:'ghin'.
// Overwrites self-reported; leaves already-verified. WRITE.
async function handlePullGhinsForField(_p, tournamentId, db) {
  const [regsSnap, rosterSnap, infoSnap] = await Promise.all([
    get(tref(`tournaments/${tournamentId}/registrations`)),
    get(tref('club/players')),
    get(tref(`tournaments/${tournamentId}/info`)),
  ])
  const registrations = regsSnap.val() || {}
  const info = infoSnap.val() || {}
  const cp = courseParamsFor(info)
  const { toPull, skipped, alreadyVerified } = planGhinPull(registrations, rosterSnap.val() || {}, { matchName: fuzzyMatchName })
  if (toPull.length === 0) {
    return { success: true, message: `No GHINs to pull — ${alreadyVerified.length} already verified, ${skipped.length} have no GHIN on file.` }
  }
  let pulled = 0
  let failed = 0
  for (const item of toPull) {
    try {
      const res = await lookupGhinHandicap(item.ghin)
      if (!res?.ok) { failed++; continue }
      const entry = buildHandicapEntry({ ghin: item.ghin, index: res.handicapIndex, source: 'ghin', ...cp })
      await updateRegistration(tournamentId, item.regId, { [`playerHandicaps/${item.playerIndex}`]: entry })
      pulled++
    } catch { failed++ }
  }
  try { await snapshotNetHandicaps(tournamentId) } catch { /* best-effort */ }
  const bits = [`Verified ${pulled} player${pulled === 1 ? '' : 's'} from GHIN`]
  if (skipped.length) bits.push(`skipped ${skipped.length} with no GHIN on file`)
  if (failed) bits.push(`${failed} lookup${failed === 1 ? '' : 's'} failed`)
  if (alreadyVerified.length) bits.push(`${alreadyVerified.length} already verified`)
  return { success: true, message: bits.join('; ') + '.' }
}

// SET_EVENT_ALLOWANCE — event-wide allowance %. WRITE.
async function handleSetEventAllowance(p, tournamentId, db) {
  const pct = Number(p?.allowancePercent)
  if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
    return { success: false, message: `Allowance must be 1–100% (got ${p?.allowancePercent}).` }
  }
  await updateTournamentInfo(tournamentId, { handicapAllowance: pct })
  try { await snapshotNetHandicaps(tournamentId) } catch { /* best-effort */ }
  return { success: true, message: `Event allowance set to ${pct}% for every player without an override.` }
}

// SET_PLAYER_ALLOWANCE — per-player allowance override. WRITE.
async function handleSetPlayerAllowance(p, tournamentId, db) {
  const pct = Number(p?.allowancePercent)
  if (!p?.playerName) return { success: false, message: 'Which player? Give me a name.' }
  if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
    return { success: false, message: `Allowance must be 1–100% (got ${p?.allowancePercent}).` }
  }
  const registrations = (await get(tref(`tournaments/${tournamentId}/registrations`))).val() || {}
  const hit = resolveRegistrationPlayer(registrations, p.playerName, fuzzyMatchName)
  if (!hit) return { success: false, message: `No registered player matching “${p.playerName}”.` }
  if (hit.ambiguous) return { success: false, message: `“${p.playerName}” matches more than one player: ${hit.ambiguous.join(', ')}. Which one?` }
  await updateRegistration(tournamentId, hit.regId, { [`playerHandicaps/${hit.playerIndex}/allowance`]: pct })
  try { await snapshotNetHandicaps(tournamentId) } catch { /* best-effort */ }
  return { success: true, message: `Gave ${hit.name} a ${pct}% allowance (overriding the event default).` }
}

// SET_SELF_REPORTED_GATE — allow self-reported, or make GHIN-required. WRITE.
async function handleSetSelfReportedGate(p, tournamentId, db) {
  if (typeof p?.allow !== 'boolean') return { success: false, message: 'Say whether to allow self-reported (true) or require GHIN (false).' }
  const patch = p.allow
    ? { allowSelfReported: true }
    : { allowSelfReported: false, handicapMode: 'required' }
  await updateTournamentInfo(tournamentId, patch)
  try { await snapshotNetHandicaps(tournamentId) } catch { /* best-effort */ }
  return {
    success: true,
    message: p.allow
      ? 'Self-reported handicaps are now allowed and count toward net.'
      : 'This is now a GHIN-required event — self-reported indexes are excluded from net.',
  }
}

// SET_TV_NET_MODE — flip the TV boards between GROSS and NET. WRITE (display).
async function handleSetTvNetMode(p, tournamentId, db) {
  if (typeof p?.net !== 'boolean') return { success: false, message: 'Say whether to show NET (true) or GROSS (false) on the TVs.' }
  await setTvNetMode(tournamentId, p.net)
  return { success: true, message: `TV boards now showing ${p.net ? 'NET' : 'GROSS'}.` }
}

// HANDICAP_REVIEW — who's self-reported / how many aren't verified. READ.
async function handleHandicapReview(_p, tournamentId, db) {
  const registrations = (await get(tref(`tournaments/${tournamentId}/registrations`))).val() || {}
  return { success: true, message: summarizeHandicapReview(collectHandicapReview(registrations)) }
}

// ─── shared resolvers for the score / bracket / flight / registration actions ─
const nrm = (s) => String(s || '').trim().toLowerCase()

// Find a team across all flights by team name OR a player name on it.
// Returns { flightId, teamId, info } or null. Exact team-name match wins;
// otherwise the first partial (substring / player) match.
function findTeamInFlights(flights, query) {
  const q = nrm(query)
  if (!q) return null
  let partial = null
  for (const [flightId, flight] of Object.entries(flights || {})) {
    if (String(flightId).startsWith('_')) continue
    for (const [teamId, team] of Object.entries(flight?.teams || {})) {
      const info = team?.info || {}
      const name = nrm(info.name)
      if (name && name === q) return { flightId, teamId, info }
      const players = (info.players || []).map((p) => nrm(typeof p === 'string' ? p : p?.name)).filter(Boolean)
      if (!partial && ((name && (name.includes(q) || q.includes(name))) || players.some((p) => p === q || p.includes(q) || q.includes(p)))) {
        partial = { flightId, teamId, info }
      }
    }
  }
  return partial
}

// Resolve a flight id from an id or a human name ("Championship", "Flight 3").
function findFlightId(flights, query) {
  if (!query) return null
  if (flights[query]) return query
  const q = nrm(query)
  for (const [flightId, flight] of Object.entries(flights || {})) {
    if (String(flightId).startsWith('_')) continue
    const name = nrm(flight?.info?.name)
    if (name && (name === q || name.includes(q) || q.includes(name))) return flightId
  }
  return null
}

// Find a registration id by team name or a player name.
function findRegByName(regs, query) {
  const q = nrm(query)
  if (!q) return null
  let partial = null
  for (const [regId, r] of Object.entries(regs || {})) {
    const name = nrm(r?.teamName)
    if (name && name === q) return regId
    const players = [...(r?.players || []), r?.player1, r?.player2].map(nrm).filter(Boolean)
    if (!partial && ((name && (name.includes(q) || q.includes(name))) || players.some((p) => p === q || p.includes(q) || q.includes(p)))) {
      partial = regId
    }
  }
  return partial
}

// Find a qualifying team id by team name / player name inside an event's teams.
function findQualTeamId(teams, query) {
  const q = nrm(query)
  if (!q) return null
  let partial = null
  for (const [teamId, t] of Object.entries(teams || {})) {
    const name = nrm(t?.teamName)
    if (name && name === q) return teamId
    const players = [t?.player1, t?.player2].map(nrm).filter(Boolean)
    if (!partial && ((name && (name.includes(q) || q.includes(name))) || players.some((p) => p === q || p.includes(q) || q.includes(p)))) {
      partial = teamId
    }
  }
  return partial
}

// ─── SET_QUALIFYING_SCORE ────────────────────────────────────────────────────
// Enter/edit a team's QUALIFYING total (and optional hole map) on the standalone
// qualifyingEvents node. Resolves the event from the tournament link, then the
// team by name. Marks it submitted so it posts in the Score column.
async function handleSetQualifyingScore(p, tournamentId, db) {
  const score = Number(p?.score)
  if (!p?.teamName) return { success: false, message: 'Which team? Give me a team or player name.' }
  if (!Number.isFinite(score) || score <= 0) return { success: false, message: `Qualifying score must be a positive number (got ${p?.score}).` }
  const info = (await get(tref(`tournaments/${tournamentId}/info`))).val() || {}
  const allEvents = (await get(tref('qualifyingEvents'))).val() || {}
  let eventId = p?.eventId || info.qualifyingEventId || null
  if (!eventId) {
    for (const [eid, ev] of Object.entries(allEvents)) {
      if (ev?.info?.sourceRegistrationId === tournamentId) { eventId = eid; break }
    }
  }
  if (!eventId) {
    for (const [eid, ev] of Object.entries(allEvents)) {
      if (findQualTeamId(ev?.teams, p.teamName)) { eventId = eid; break }
    }
  }
  if (!eventId) return { success: false, message: 'No qualifying event is linked to this tournament.' }
  const teams = (await get(tref(`qualifyingEvents/${eventId}/teams`))).val() || {}
  const teamId = findQualTeamId(teams, p.teamName)
  if (!teamId) return { success: false, message: `No qualifying team matching “${p.teamName}”.` }
  const holesOut = {}
  const rawHoles = p?.holes || {}
  for (const [h, v] of Object.entries(rawHoles)) { const n = Number(v); if (Number.isFinite(n) && n > 0) holesOut[h] = n }
  await submitQualifyingEventResult(eventId, teamId, { score, holes: holesOut, teamName: teams[teamId]?.teamName })
  return { success: true, message: `Set ${teams[teamId]?.teamName || p.teamName}'s qualifying score to ${score}.` }
}

// ─── SET_LIVE_SCORE ──────────────────────────────────────────────────────────
// Enter/edit a team's LIVE per-hole score on the built tournament (best-ball).
async function handleSetLiveScore(p, tournamentId, db) {
  const hole = Number(p?.hole)
  const score = Number(p?.score)
  if (!Number.isFinite(hole) || hole < 1 || hole > 18) return { success: false, message: `Hole must be 1–18 (got ${p?.hole}).` }
  if (!Number.isFinite(score) || score <= 0) return { success: false, message: `Score must be a positive number (got ${p?.score}).` }
  let flightId = p?.flightId, teamId = p?.teamId
  if (!flightId || !teamId) {
    const flights = (await get(tref(`tournaments/${tournamentId}/flights`))).val() || {}
    const hit = findTeamInFlights(flights, p?.teamName)
    if (!hit) return { success: false, message: `No team matching “${p?.teamName || '(unnamed)'}”.` }
    flightId = hit.flightId; teamId = hit.teamId
  }
  await setTeamHoleScore(tournamentId, flightId, teamId, hole, score)
  return { success: true, message: `Set hole ${hole} to ${score} for ${p?.teamName || teamId}.` }
}

// ─── SET_MATCH_WINNER ────────────────────────────────────────────────────────
// Advance / correct a bracket result: mark the winner of the match the two
// teams are in, then propagate downstream.
async function handleSetMatchWinner(p, tournamentId, db) {
  const flights = (await get(tref(`tournaments/${tournamentId}/flights`))).val() || {}
  const winner = findTeamInFlights(flights, p?.winnerTeamName)
  if (!winner) return { success: false, message: `No team matching winner “${p?.winnerTeamName || ''}”.` }
  let flightId = p?.flightId ? (findFlightId(flights, p.flightId) || winner.flightId) : winner.flightId
  const loser = p?.loserTeamName ? findTeamInFlights(flights, p.loserTeamName) : null
  const brackets = (await get(tref(`tournaments/${tournamentId}/brackets/${flightId}`))).val() || {}
  const rounds = brackets.rounds || {}
  let found = null
  for (const [roundKey, round] of Object.entries(rounds)) {
    for (const [matchId, m] of Object.entries(round?.matches || {})) {
      const inMatch = m?.a === winner.teamId || m?.b === winner.teamId
      const loserOk = !loser || m?.a === loser.teamId || m?.b === loser.teamId
      if (inMatch && loserOk) { found = { roundKey, matchId }; if (loser) break }
    }
    if (found && loser) break
  }
  if (!found) return { success: false, message: `Could not find a bracket match with ${winner.info?.name || 'that team'} in ${flightId}.` }
  await setBracketMatch(tournamentId, flightId, found.roundKey, found.matchId, { winnerId: winner.teamId })
  await advanceBracket(tournamentId, flightId)
  return { success: true, message: `${winner.info?.name || 'Winner'} advanced in ${flightId} (${found.roundKey}).` }
}

// ─── MOVE_TEAM ───────────────────────────────────────────────────────────────
// Move a WHOLE team into another flight. Clears the old pairing (opponentId is
// flight-local); brackets aren't auto-rebuilt.
async function handleMoveTeam(p, tournamentId, db) {
  const flights = (await get(tref(`tournaments/${tournamentId}/flights`))).val() || {}
  let src = (p?.fromFlightId && p?.fromTeamId)
    ? { flightId: p.fromFlightId, teamId: p.fromTeamId, info: flights?.[p.fromFlightId]?.teams?.[p.fromTeamId]?.info }
    : findTeamInFlights(flights, p?.teamName)
  if (!src || !src.info) return { success: false, message: `No team matching “${p?.teamName || ''}”.` }
  const destFlightId = findFlightId(flights, p?.toFlightId || p?.toFlightName)
  if (!destFlightId) return { success: false, message: `No destination flight matching “${p?.toFlightId || p?.toFlightName || ''}”.` }
  if (destFlightId === src.flightId) return { success: false, message: 'That team is already in that flight.' }
  const teamNode = (await get(tref(`tournaments/${tournamentId}/flights/${src.flightId}/teams/${src.teamId}`))).val() || {}
  // Clear the moved team's pairing + any former opponent's back-reference.
  const oldOppId = teamNode?.info?.opponentId
  if (teamNode.info) teamNode.info = { ...teamNode.info, opponentId: null, startingHole: teamNode.info.startingHole ?? null }
  const updates = {
    [`tournaments/${tournamentId}/flights/${src.flightId}/teams/${src.teamId}`]: null,
    [`tournaments/${tournamentId}/flights/${destFlightId}/teams/${src.teamId}`]: teamNode,
  }
  if (oldOppId) updates[`tournaments/${tournamentId}/flights/${src.flightId}/teams/${oldOppId}/info/opponentId`] = null
  await updateTenant(updates)
  return { success: true, message: `Moved ${src.info?.name || src.teamId} to ${flights?.[destFlightId]?.info?.name || destFlightId}. Re-pair the affected flights if needed.` }
}

// ─── SET_MATCHUP ─────────────────────────────────────────────────────────────
// Change who plays who inside a flight — set two teams as each other's opponent.
async function handleSetMatchup(p, tournamentId, db) {
  const flights = (await get(tref(`tournaments/${tournamentId}/flights`))).val() || {}
  const t1 = findTeamInFlights(flights, p?.team1Name)
  const t2 = findTeamInFlights(flights, p?.team2Name)
  if (!t1) return { success: false, message: `No team matching “${p?.team1Name || ''}”.` }
  if (!t2) return { success: false, message: `No team matching “${p?.team2Name || ''}”.` }
  if (t1.flightId !== t2.flightId) return { success: false, message: 'Both teams must be in the same flight to be matched up. Move one first.' }
  if (t1.teamId === t2.teamId) return { success: false, message: 'Pick two different teams.' }
  const base = `tournaments/${tournamentId}/flights/${t1.flightId}/teams`
  // Break any stale back-references from the teams they were previously paired with.
  const updates = {
    [`${base}/${t1.teamId}/info/opponentId`]: t2.teamId,
    [`${base}/${t2.teamId}/info/opponentId`]: t1.teamId,
  }
  const oldOpp1 = t1.info?.opponentId, oldOpp2 = t2.info?.opponentId
  if (oldOpp1 && oldOpp1 !== t2.teamId) updates[`${base}/${oldOpp1}/info/opponentId`] = null
  if (oldOpp2 && oldOpp2 !== t1.teamId) updates[`${base}/${oldOpp2}/info/opponentId`] = null
  await updateTenant(updates)
  return { success: true, message: `${t1.info?.name || t1.teamId} now plays ${t2.info?.name || t2.teamId} in ${flights?.[t1.flightId]?.info?.name || t1.flightId}.` }
}

// ─── RENAME_TEAM ─────────────────────────────────────────────────────────────
// Rename a team on the registration AND (if built) on the flight team.
async function handleRenameTeam(p, tournamentId, db) {
  if (!p?.newTeamName) return { success: false, message: 'What should the new team name be?' }
  if (!p?.oldTeamName) return { success: false, message: 'Which team should I rename?' }
  const did = []
  const regs = (await get(tref(`tournaments/${tournamentId}/registrations`))).val() || {}
  const regId = findRegByName(regs, p.oldTeamName)
  if (regId) { await updateRegistration(tournamentId, regId, { teamName: p.newTeamName }); did.push('registration') }
  const flights = (await get(tref(`tournaments/${tournamentId}/flights`))).val() || {}
  const hit = findTeamInFlights(flights, p.oldTeamName)
  if (hit) { await update(tref(`tournaments/${tournamentId}/flights/${hit.flightId}/teams/${hit.teamId}/info`), { name: p.newTeamName }); did.push('flight team') }
  if (did.length === 0) return { success: false, message: `No team matching “${p.oldTeamName}”.` }
  return { success: true, message: `Renamed “${p.oldTeamName}” to “${p.newTeamName}” (${did.join(' + ')}).` }
}

// ─── ADD_REGISTRATION ────────────────────────────────────────────────────────
async function handleAddRegistration(p, tournamentId, db) {
  const players = Array.isArray(p?.players) ? p.players.filter(Boolean) : [p?.player1, p?.player2].filter(Boolean)
  const teamName = (p?.teamName && String(p.teamName).trim()) || players.join(' / ')
  if (!teamName && players.length === 0) return { success: false, message: 'Give me a team name and/or players to add.' }
  const reg = { teamName: teamName || 'Team', qualifyingStatus: 'pending' }
  if (players.length) reg.players = players
  await addRegistration(tournamentId, reg)
  return { success: true, message: `Added registration “${teamName}”${players.length ? ` (${players.join(' & ')})` : ''}.` }
}

// ─── REMOVE_REGISTRATION ─────────────────────────────────────────────────────
async function handleRemoveRegistration(p, tournamentId, db) {
  if (!p?.teamName) return { success: false, message: 'Which team registration should I remove?' }
  const regs = (await get(tref(`tournaments/${tournamentId}/registrations`))).val() || {}
  const regId = findRegByName(regs, p.teamName)
  if (!regId) return { success: false, message: `No registration matching “${p.teamName}”.` }
  const name = regs[regId]?.teamName || p.teamName
  await deleteRegistration(tournamentId, regId)
  return { success: true, message: `Removed registration “${name}”.` }
}
